/**
 * Windows Installation Guide Page
 * Content synced from docs/tutorials/windows-install.html
 */

import { useState } from 'react';
import { ExternalLink, Download } from 'lucide-react';
import { useI18n, Locale } from '@/lib/i18n';
import { Lightbox } from '@/components/docs/Lightbox';
import './docs.scss';

interface Subsection {
  title: string;
  content: string;
}

interface Step {
  title: string;
  content: string;
  subsections?: Subsection[];
  warning?: string;
  info?: string;
  success?: string;
}

interface TroubleshootingIssue {
  title: string;
  content: string;
}

interface WindowsInstallData {
  pageTitle: string;
  overview: string;
  downloadButton: string;
  requirements: {
    title: string;
    items: string[];
  };
  steps: Step[];
  troubleshooting: {
    title: string;
    issues: TroubleshootingIssue[];
  };
}

const translations: Record<Locale, WindowsInstallData> = {
  en: {
    pageTitle: 'Windows Installation Guide',
    overview:
      'This guide will walk you through installing Sokuji on Windows systems. Sokuji is available as a .exe installer for easy installation on Windows 10 and Windows 11.',
    downloadButton: 'Download for Windows',
    requirements: {
      title: 'System Requirements',
      items: [
        'Windows 10 (version 1903 or later) or Windows 11',
        '64-bit processor',
        '4GB RAM minimum (8GB recommended)',
        '200MB available disk space',
        'Internet connection for AI translation services',
        'Microphone and speakers/headphones',
      ],
    },
    steps: [
      {
        title: 'Step 1: Download Sokuji',
        content: `Visit the <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">official GitHub releases page</a> to download the latest version of Sokuji for Windows.`,
        subsections: [
          {
            title: 'Choose the Right Installer',
            content: `<ul>
              <li><strong>.exe installer</strong> - Standard installer for Windows</li>
              <li><strong>Portable version</strong> - No installation required (if available)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Step 2: Run the Installer',
        content: `Once downloaded, locate the installer file and run it to begin installation.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>When you run the installer, Windows Defender SmartScreen may prevent the app from starting:</p>
            <img src="/tutorials/windows-install/1.png" alt="Windows Defender SmartScreen warning" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Click <strong>"More info"</strong> on the SmartScreen warning</li>
              <li>The window will expand to show more details:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Windows Defender SmartScreen expanded view" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Click <strong>"Run anyway"</strong> to proceed with the installation</li>
            </ol>`,
          },
        ],
        warning:
          'The Sokuji installer is code-signed through SignPath Foundation. If SmartScreen still warns about it, the steps above let you continue.',
      },
      {
        title: 'Step 3: Install VB-CABLE Virtual Audio Device',
        content: `Sokuji requires VB-CABLE virtual audio device to route audio to other applications. The installer will prompt you to install it automatically.`,
        subsections: [
          {
            title: 'VB-CABLE Installation',
            content: `<p>After bypassing SmartScreen, you'll see the VB-CABLE installation prompt:</p>
            <img src="/tutorials/windows-install/3.png" alt="VB-CABLE installation prompt" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Click <strong>"Install Now"</strong> to automatically download and install VB-CABLE</li>
              <li>Alternatively, you can click "Download Manually" to get it from <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>When the User Account Control prompt appears, click <strong>"Yes"</strong> to allow the installation:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="User Account Control prompt for VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>The VB-CABLE installer will open. Click <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="VB-CABLE installer window" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Wait for the installation to complete. You'll see a success message:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="VB-CABLE installation complete" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Click <strong>"OK"</strong> on the installation complete dialog</li>
              <li>The final confirmation will appear:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="VB-CABLE installation success confirmation" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Click <strong>"OK"</strong> to finish the VB-CABLE installation</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE creates virtual audio devices that allow Sokuji to pass translated audio to video conferencing applications like Zoom, Teams, or Google Meet.',
      },
      {
        title: 'Step 4: First Run Setup',
        content: `After VB-CABLE installation, Sokuji will launch automatically and you'll see the audio configuration page:`,
        subsections: [
          {
            title: 'Verify Audio Devices',
            content: `<img src="/tutorials/windows-install/7.png" alt="Sokuji audio configuration showing VB-CABLE devices" class="install-page__screenshot" data-lightbox="true" />
            <p>To verify VB-CABLE was installed successfully, open <strong>Windows Sound Settings</strong> (right-click the speaker icon in the system tray → "Sound settings"):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - Should appear under Recording devices</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - Should appear under Playback devices</li>
            </ul>
            <p><strong>Note:</strong> You will not see these CABLE devices in Sokuji's device list — this is by design. Sokuji hides them to prevent accidental audio feedback loops, and automatically detects and uses VB-CABLE in the background.</p>`,
          },
          {
            title: 'Complete Setup',
            content: `<ol>
              <li>Configure your preferred AI provider (OpenAI, Google Gemini, etc.)</li>
              <li>Enter your API key for the selected provider</li>
              <li>Select source and target languages</li>
              <li>Test the audio input/output devices</li>
              <li>Select your physical microphone under <strong>Microphone</strong></li>
              <li>In the <strong>Speech</strong> block, choose your speakers or headphones as the <strong>Default playback device</strong>. <strong>I hear it too</strong> and <strong>Translation I hear</strong> can each play on their own device; <strong>Translation the other side hears</strong> goes into the virtual microphone, CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji is now ready to use! You can start real-time translation by clicking the start session button.',
      },
    ],
    troubleshooting: {
      title: 'Troubleshooting',
      issues: [
        {
          title: 'Windows Defender blocks the installation',
          content: `If Windows Defender SmartScreen blocks the installation:
          <ol>
            <li>Click "More info" on the SmartScreen warning</li>
            <li>Click "Run anyway" to proceed with installation</li>
          </ol>
          SmartScreen can warn about a signed installer it has not seen often. This does not indicate a security problem.`,
        },
        {
          title: 'Microphone not detected or will not open',
          content: `When the microphone will not open at Start, Sokuji says why:
          <ul>
            <li><strong>"The microphone “…” is in use by …"</strong>: another app is holding it. Close the app it names and start again, or click <strong>Settings</strong> and choose another microphone.</li>
            <li><strong>"Windows could not open the microphone “…”"</strong>: another app may be holding it, or its driver stopped responding. Close apps that use it or reconnect it, then start again; <strong>Settings</strong> lets you choose another microphone.</li>
            <li><strong>"Windows is not letting Sokuji use the microphone"</strong>: Windows' privacy settings keep desktop apps off the microphone. Click <strong>Open System Settings</strong>, turn on microphone access for desktop apps, then start again.</li>
          </ul>
          If your microphone is not listed at all:
          <ol>
            <li>Open Windows Settings → Privacy & security → Microphone</li>
            <li>Ensure microphone access and "Let desktop apps access your microphone" are on</li>
            <li>Check your microphone is properly connected and set as default device</li>
          </ol>`,
        },
        {
          title: 'No audio output',
          content: `If you're not hearing any audio:
          <ol>
            <li>Right-click the speaker icon in system tray</li>
            <li>Select "Open Sound settings"</li>
            <li>Verify your output device is correctly selected</li>
            <li>Check the volume levels are not muted</li>
            <li>In Sokuji's <strong>Speech</strong> block, check the device of the row you expect to hear: <strong>I hear it too</strong> for your own translation (off by default), <strong>Translation I hear</strong> for the other side's, and the <strong>Default playback device</strong> for everything else</li>
          </ol>`,
        },
        {
          title: "Application won't start",
          content: `If Sokuji won't start:
          <ul>
            <li>Try running as Administrator (right-click → Run as administrator)</li>
            <li>Check if antivirus software is blocking the application</li>
            <li>Reinstall the application</li>
            <li>Ensure you have the latest Windows updates installed</li>
          </ul>`,
        },
        {
          title: 'VB-CABLE devices not detected',
          content: `If you don't see CABLE Output/Input devices in Sokuji:
          <ol>
            <li><strong>Restart your computer</strong> - This often resolves device recognition issues</li>
            <li><strong>Check Windows Sound Settings</strong>:
              <ul>
                <li>Right-click the speaker icon in system tray</li>
                <li>Select "Sound settings"</li>
                <li>Look for "CABLE Input" in playback devices</li>
                <li>Look for "CABLE Output" in recording devices</li>
              </ul>
            </li>
            <li><strong>Manually reinstall VB-CABLE</strong>:
              <ul>
                <li>Download from <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Extract the ZIP file</li>
                <li>Run VBCABLE_Setup_x64.exe as Administrator</li>
                <li>Restart your computer after installation</li>
              </ul>
            </li>
            <li><strong>Check Device Manager</strong>:
              <ul>
                <li>Open Device Manager (Win+X → Device Manager)</li>
                <li>Look under "Sound, video and game controllers"</li>
                <li>Verify "VB-Audio Virtual Cable" is listed without errors</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  zh: {
    pageTitle: 'Windows 安装指南',
    overview:
      '本指南将指导您在 Windows 系统上安装 Sokuji。Sokuji 提供 .exe 安装程序，可在 Windows 10 和 Windows 11 上轻松安装。',
    downloadButton: '下载 Windows 版',
    requirements: {
      title: '系统要求',
      items: [
        'Windows 10（1903版本或更高）或 Windows 11',
        '64位处理器',
        '最低4GB内存（推荐8GB）',
        '200MB可用磁盘空间',
        '用于AI翻译服务的互联网连接',
        '麦克风和扬声器/耳机',
      ],
    },
    steps: [
      {
        title: '步骤 1：下载 Sokuji',
        content: `访问 <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">官方 GitHub 发布页面</a> 下载最新的 Windows 版本 Sokuji。`,
        subsections: [
          {
            title: '选择正确的安装程序',
            content: `<ul>
              <li><strong>.exe 安装程序</strong> - Windows 的标准安装程序</li>
              <li><strong>便携版</strong> - 无需安装（如果可用）</li>
            </ul>`,
          },
        ],
      },
      {
        title: '步骤 2：运行安装程序',
        content: `下载完成后，找到安装文件并运行它开始安装。`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>运行安装程序时，Windows Defender SmartScreen 可能会阻止应用启动：</p>
            <img src="/tutorials/windows-install/1.png" alt="Windows Defender SmartScreen 警告" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>点击 SmartScreen 警告上的<strong>"更多信息"</strong></li>
              <li>窗口将展开显示更多详情：</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Windows Defender SmartScreen 展开视图" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>点击<strong>"仍要运行"</strong>继续安装</li>
            </ol>`,
          },
        ],
        warning:
          'Sokuji 安装程序已由 SignPath Foundation 代码签名。如果 SmartScreen 仍然显示警告，按上面的步骤继续即可。',
      },
      {
        title: '步骤 3：安装 VB-CABLE 虚拟音频设备',
        content: `Sokuji 需要 VB-CABLE 虚拟音频设备来将音频路由到其他应用程序。安装程序会提示您自动安装它。`,
        subsections: [
          {
            title: 'VB-CABLE 安装',
            content: `<p>绕过 SmartScreen 后，您会看到 VB-CABLE 安装提示：</p>
            <img src="/tutorials/windows-install/3.png" alt="VB-CABLE 安装提示" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>点击<strong>"Install Now"</strong>自动下载并安装 VB-CABLE</li>
              <li>或者，您可以点击"Download Manually"从 <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a> 手动获取</li>
              <li>当用户账户控制提示出现时，点击<strong>"是"</strong>允许安装：</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="VB-CABLE 的用户账户控制提示" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>VB-CABLE 安装程序将打开。点击<strong>"Install Driver"</strong>：</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="VB-CABLE 安装程序窗口" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>等待安装完成。您会看到成功消息：</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="VB-CABLE 安装完成" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>在安装完成对话框上点击<strong>"OK"</strong></li>
              <li>最终确认将出现：</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="VB-CABLE 安装成功确认" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>点击<strong>"OK"</strong>完成 VB-CABLE 安装</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE 创建虚拟音频设备，允许 Sokuji 将翻译后的音频传递给 Zoom、Teams 或 Google Meet 等视频会议应用程序。',
      },
      {
        title: '步骤 4：首次运行设置',
        content: `VB-CABLE 安装后，Sokuji 将自动启动，您会看到音频配置页面：`,
        subsections: [
          {
            title: '验证音频设备',
            content: `<img src="/tutorials/windows-install/7.png" alt="显示 VB-CABLE 设备的 Sokuji 音频配置" class="install-page__screenshot" data-lightbox="true" />
            <p>要验证 VB-CABLE 是否安装成功，请打开 <strong>Windows 声音设置</strong>（右键点击系统托盘中的扬声器图标 → "声音设置"）：</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - 应出现在录制设备中</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - 应出现在播放设备中</li>
            </ul>
            <p><strong>注意：</strong>您不会在 Sokuji 的设备列表中看到这些 CABLE 设备——这是正常的。Sokuji 隐藏了这些设备以防止意外的音频反馈环路，并会在后台自动检测和使用 VB-CABLE。</p>`,
          },
          {
            title: '完成设置',
            content: `<ol>
              <li>配置您首选的 AI 提供商（OpenAI、Google Gemini 等）</li>
              <li>输入所选提供商的 API 密钥</li>
              <li>选择源语言和目标语言</li>
              <li>测试音频输入/输出设备</li>
              <li>在<strong>麦克风</strong>中选择您的物理麦克风</li>
              <li>在<strong>语音</strong>区块中，把<strong>默认播放设备</strong>设为您的扬声器或耳机。<strong>我也听</strong>和<strong>我听到的翻译</strong>可以各自选择播放设备；<strong>对方听到的翻译</strong>会送进虚拟麦克风 CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success: 'Sokuji 现在已准备就绪！您可以通过点击麦克风按钮开始实时翻译。',
      },
    ],
    troubleshooting: {
      title: '故障排除',
      issues: [
        {
          title: 'Windows Defender 阻止安装',
          content: `如果 Windows Defender SmartScreen 阻止安装：
          <ol>
            <li>在 SmartScreen 警告上点击"更多信息"</li>
            <li>点击"仍要运行"继续安装</li>
          </ol>
          对于不常见的已签名安装程序，SmartScreen 也可能显示警告，这并不代表存在安全问题。`,
        },
        {
          title: '未检测到麦克风或麦克风无法打开',
          content: `开始会话时如果麦克风无法打开，Sokuji 会说明原因：
          <ul>
            <li><strong>“麦克风「…」正被 … 占用”</strong>：有其他应用占用了它。请关闭提示中列出的应用后重新开始，或点击<strong>设置</strong>选择其他麦克风。</li>
            <li><strong>“Windows 无法打开麦克风「…」”</strong>：可能有其他应用占用了它，或者它的驱动没有响应。请关闭正在使用它的应用或重新连接麦克风，然后重新开始；也可以点击<strong>设置</strong>选择其他麦克风。</li>
            <li><strong>“Windows 不允许 Sokuji 使用麦克风”</strong>：Windows 的隐私设置禁止桌面应用使用麦克风。请点击<strong>打开系统设置</strong>，打开桌面应用的麦克风访问权限，然后重新开始。</li>
          </ul>
          如果麦克风根本没有出现在列表中：
          <ol>
            <li>打开 Windows 设置 → 隐私和安全性 → 麦克风</li>
            <li>确保“麦克风访问权限”和“允许桌面应用访问你的麦克风”都已打开</li>
            <li>检查麦克风是否正确连接并设置为默认设备</li>
          </ol>`,
        },
        {
          title: '无音频输出',
          content: `如果听不到任何音频：
          <ol>
            <li>右键点击系统托盘中的扬声器图标</li>
            <li>选择"打开声音设置"</li>
            <li>验证输出设备是否正确选择</li>
            <li>检查音量级别是否未静音</li>
            <li>在 Sokuji 的<strong>语音</strong>区块中，检查您想听到的那一行所选的设备：您自己的翻译看<strong>我也听</strong>（默认关闭），对方的翻译看<strong>我听到的翻译</strong>，其他声音看<strong>默认播放设备</strong></li>
          </ol>`,
        },
        {
          title: '应用程序无法启动',
          content: `如果 Sokuji 无法启动：
          <ul>
            <li>尝试以管理员身份运行（右键 → 以管理员身份运行）</li>
            <li>检查防病毒软件是否阻止了应用程序</li>
            <li>重新安装应用程序</li>
            <li>确保已安装最新的 Windows 更新</li>
          </ul>`,
        },
        {
          title: '未检测到 VB-CABLE 设备',
          content: `如果在 Sokuji 中看不到 CABLE Output/Input 设备：
          <ol>
            <li><strong>重启计算机</strong> - 这通常可以解决设备识别问题</li>
            <li><strong>检查 Windows 声音设置</strong>：
              <ul>
                <li>右键点击系统托盘中的扬声器图标</li>
                <li>选择"声音设置"</li>
                <li>在播放设备中查找"CABLE Input"</li>
                <li>在录制设备中查找"CABLE Output"</li>
              </ul>
            </li>
            <li><strong>手动重新安装 VB-CABLE</strong>：
              <ul>
                <li>从 <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a> 下载</li>
                <li>解压 ZIP 文件</li>
                <li>以管理员身份运行 VBCABLE_Setup_x64.exe</li>
                <li>安装后重启计算机</li>
              </ul>
            </li>
            <li><strong>检查设备管理器</strong>：
              <ul>
                <li>打开设备管理器（Win+X → 设备管理器）</li>
                <li>在"声音、视频和游戏控制器"下查看</li>
                <li>验证"VB-Audio Virtual Cable"是否列出且无错误</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  ja: {
    pageTitle: 'Windows インストールガイド',
    overview:
      'このガイドでは、Windows システムに Sokuji をインストールする方法を説明します。Sokuji は Windows 10 と Windows 11 で簡単にインストールできる .exe インストーラーとして提供されています。',
    downloadButton: 'Windows 版をダウンロード',
    requirements: {
      title: 'システム要件',
      items: [
        'Windows 10（バージョン1903以降）またはWindows 11',
        '64ビットプロセッサ',
        '最小4GBのRAM（8GB推奨）',
        '200MBの空きディスク容量',
        'AI翻訳サービス用のインターネット接続',
        'マイクとスピーカー/ヘッドフォン',
      ],
    },
    steps: [
      {
        title: 'ステップ 1：Sokuji をダウンロード',
        content: `<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">公式 GitHub リリースページ</a>にアクセスして、Windows 用の Sokuji の最新バージョンをダウンロードします。`,
        subsections: [
          {
            title: '適切なインストーラーを選択',
            content: `<ul>
              <li><strong>.exe インストーラー</strong> - Windows 用の標準インストーラー</li>
              <li><strong>ポータブル版</strong> - インストール不要（利用可能な場合）</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'ステップ 2：インストーラーを実行',
        content: `ダウンロードが完了したら、インストーラーファイルを見つけて実行し、インストールを開始します。`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>インストーラーを実行すると、Windows Defender SmartScreen がアプリの起動を阻止する場合があります：</p>
            <img src="/tutorials/windows-install/1.png" alt="Windows Defender SmartScreen 警告" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>SmartScreen 警告で<strong>「詳細情報」</strong>をクリック</li>
              <li>ウィンドウが展開して詳細が表示されます：</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Windows Defender SmartScreen 展開ビュー" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li><strong>「実行」</strong>をクリックしてインストールを続行</li>
            </ol>`,
          },
        ],
        warning:
          'Sokuji のインストーラーは SignPath Foundation によりコード署名されています。それでも SmartScreen の警告が出た場合は、上の手順で続行できます。',
      },
      {
        title: 'ステップ 3：VB-CABLE 仮想オーディオデバイスをインストール',
        content: `Sokuji は、他のアプリケーションにオーディオをルーティングするために VB-CABLE 仮想オーディオデバイスが必要です。インストーラーは自動的にインストールするように促します。`,
        subsections: [
          {
            title: 'VB-CABLE インストール',
            content: `<p>SmartScreen を回避した後、VB-CABLE インストールプロンプトが表示されます：</p>
            <img src="/tutorials/windows-install/3.png" alt="VB-CABLE インストールプロンプト" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li><strong>「Install Now」</strong>をクリックして VB-CABLE を自動的にダウンロードしてインストール</li>
              <li>または、「Download Manually」をクリックして <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a> から手動で取得</li>
              <li>ユーザーアカウント制御のプロンプトが表示されたら、<strong>「はい」</strong>をクリックしてインストールを許可：</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="VB-CABLE のユーザーアカウント制御プロンプト" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>VB-CABLE インストーラーが開きます。<strong>「Install Driver」</strong>をクリック：</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="VB-CABLE インストーラーウィンドウ" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>インストールが完了するまで待ちます。成功メッセージが表示されます：</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="VB-CABLE インストール完了" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>インストール完了ダイアログで<strong>「OK」</strong>をクリック</li>
              <li>最終確認が表示されます：</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="VB-CABLE インストール成功確認" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li><strong>「OK」</strong>をクリックして VB-CABLE インストールを完了</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE は、Sokuji が翻訳された音声を Zoom、Teams、Google Meet などのビデオ会議アプリケーションに渡すことを可能にする仮想オーディオデバイスを作成します。',
      },
      {
        title: 'ステップ 4：初回起動設定',
        content: `VB-CABLE インストール後、Sokuji が自動的に起動し、オーディオ構成ページが表示されます：`,
        subsections: [
          {
            title: 'オーディオデバイスの確認',
            content: `<img src="/tutorials/windows-install/7.png" alt="VB-CABLE デバイスを表示する Sokuji オーディオ構成" class="install-page__screenshot" data-lightbox="true" />
            <p>VB-CABLE が正常にインストールされたことを確認するには、<strong>Windows サウンド設定</strong>を開いてください（システムトレイのスピーカーアイコンを右クリック →「サウンド設定」）：</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - 録音デバイスに表示されるはずです</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - 再生デバイスに表示されるはずです</li>
            </ul>
            <p><strong>注意：</strong>Sokuji のデバイスリストにはこれらの CABLE デバイスは表示されません。これは仕様です。Sokuji は意図しないオーディオフィードバックループを防ぐためにこれらのデバイスを非表示にし、バックグラウンドで自動的に VB-CABLE を検出して使用します。</p>`,
          },
          {
            title: '設定を完了',
            content: `<ol>
              <li>お好みの AI プロバイダー（OpenAI、Google Gemini など）を設定</li>
              <li>選択したプロバイダーの API キーを入力</li>
              <li>ソース言語とターゲット言語を選択</li>
              <li>オーディオ入力/出力デバイスをテスト</li>
              <li><strong>マイクロフォン</strong>で物理マイクを選択</li>
              <li><strong>音声</strong>ブロックで、<strong>既定の再生デバイス</strong>にスピーカーまたはヘッドフォンを選択。<strong>自分にも再生</strong>と<strong>自分に聞こえる翻訳</strong>はそれぞれ別のデバイスで再生できます。<strong>相手に聞こえる翻訳</strong>は仮想マイク CABLE Output (VB-Audio Virtual Cable) に送られます</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji の準備が整いました！セッション開始ボタンをクリックしてリアルタイム翻訳を開始できます。',
      },
    ],
    troubleshooting: {
      title: 'トラブルシューティング',
      issues: [
        {
          title: 'Windows Defenderがインストールをブロックする',
          content: `Windows Defender SmartScreenがインストールをブロックする場合：
          <ol>
            <li>SmartScreen警告で「詳細情報」をクリック</li>
            <li>「実行」をクリックしてインストールを続行</li>
          </ol>
          SmartScreen は、署名済みでも見かける機会の少ないインストーラーに警告を出すことがあります。セキュリティ上の問題を示すものではありません。`,
        },
        {
          title: 'マイクが検出されない、または開けない',
          content: `セッション開始時にマイクを開けないと、Sokuji がその理由を表示します：
          <ul>
            <li><strong>『マイク「…」は…が使用中です』</strong>：ほかのアプリがマイクを使っています。表示されたアプリを終了してからもう一度開始するか、<strong>設定</strong>をクリックして別のマイクを選んでください。</li>
            <li><strong>『Windows がマイク「…」を開けませんでした』</strong>：ほかのアプリが使用中か、ドライバーが応答していない可能性があります。マイクを使っているアプリを終了するか、マイクを接続し直してから、もう一度開始してください。<strong>設定</strong>から別のマイクを選ぶこともできます。</li>
            <li><strong>『Windows の設定により、Sokuji がマイクを使用できません』</strong>：Windows のプライバシー設定でデスクトップ アプリのマイク使用がオフになっています。<strong>システム設定を開く</strong>をクリックし、デスクトップ アプリのマイクへのアクセスをオンにしてから、もう一度開始してください。</li>
          </ul>
          マイクがまったく一覧に表示されない場合：
          <ol>
            <li>Windows設定 → プライバシーとセキュリティ → マイクを開く</li>
            <li>「マイクへのアクセス」と「デスクトップ アプリがマイクにアクセスできるようにする」がオンになっていることを確認</li>
            <li>マイクが正しく接続され、デフォルトデバイスとして設定されていることを確認</li>
          </ol>`,
        },
        {
          title: 'オーディオ出力がない',
          content: `オーディオが聞こえない場合：
          <ol>
            <li>システムトレイのスピーカーアイコンを右クリック</li>
            <li>「サウンド設定を開く」を選択</li>
            <li>出力デバイスが正しく選択されていることを確認</li>
            <li>音量レベルがミュートされていないことを確認</li>
            <li>Sokuji の<strong>音声</strong>ブロックで、聞きたい行のデバイスを確認：自分の翻訳は<strong>自分にも再生</strong>（既定ではオフ）、相手の翻訳は<strong>自分に聞こえる翻訳</strong>、それ以外の音は<strong>既定の再生デバイス</strong></li>
          </ol>`,
        },
        {
          title: 'アプリケーションが起動しない',
          content: `Sokujiが起動しない場合：
          <ul>
            <li>管理者として実行してみる（右クリック → 管理者として実行）</li>
            <li>ウイルス対策ソフトウェアがアプリケーションをブロックしていないか確認</li>
            <li>アプリケーションを再インストール</li>
            <li>最新のWindows更新プログラムがインストールされていることを確認</li>
          </ul>`,
        },
        {
          title: 'VB-CABLE デバイスが検出されない',
          content: `Sokuji で CABLE Output/Input デバイスが表示されない場合：
          <ol>
            <li><strong>コンピューターを再起動</strong> - これは通常、デバイス認識の問題を解決します</li>
            <li><strong>Windows サウンド設定を確認</strong>：
              <ul>
                <li>システムトレイのスピーカーアイコンを右クリック</li>
                <li>「サウンド設定」を選択</li>
                <li>再生デバイスで「CABLE Input」を探す</li>
                <li>録音デバイスで「CABLE Output」を探す</li>
              </ul>
            </li>
            <li><strong>VB-CABLE を手動で再インストール</strong>：
              <ul>
                <li><a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a> からダウンロード</li>
                <li>ZIP ファイルを解凍</li>
                <li>管理者として VBCABLE_Setup_x64.exe を実行</li>
                <li>インストール後にコンピューターを再起動</li>
              </ul>
            </li>
            <li><strong>デバイスマネージャーを確認</strong>：
              <ul>
                <li>デバイスマネージャーを開く（Win+X → デバイスマネージャー）</li>
                <li>「サウンド、ビデオ、およびゲームコントローラー」の下を確認</li>
                <li>「VB-Audio Virtual Cable」がエラーなしでリストされていることを確認</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  ko: {
    pageTitle: 'Windows 설치 가이드',
    overview:
      '이 가이드는 Windows 시스템에서 Sokuji를 설치하는 과정을 안내합니다. Sokuji는 Windows 10 및 Windows 11에서 손쉽게 설치할 수 있는 .exe 설치 프로그램으로 제공됩니다.',
    downloadButton: 'Windows용 다운로드',
    requirements: {
      title: '시스템 요구 사항',
      items: [
        'Windows 10 (버전 1903 이상) 또는 Windows 11',
        '64비트 프로세서',
        '최소 4GB RAM (8GB 권장)',
        '200MB 이상의 사용 가능한 디스크 공간',
        'AI 번역 서비스를 위한 인터넷 연결',
        '마이크와 스피커/헤드폰',
      ],
    },
    steps: [
      {
        title: '단계 1: Sokuji 다운로드',
        content: `최신 Windows용 Sokuji를 다운로드하려면 <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">공식 GitHub 릴리스 페이지</a>를 방문하세요.`,
        subsections: [
          {
            title: '올바른 설치 프로그램 선택',
            content: `<ul>
              <li><strong>.exe 설치 프로그램</strong> - Windows용 표준 설치 프로그램</li>
              <li><strong>포터블 버전</strong> - 설치가 필요 없음 (제공되는 경우)</li>
            </ul>`,
          },
        ],
      },
      {
        title: '단계 2: 설치 프로그램 실행',
        content: `다운로드가 완료되면 설치 파일을 찾아 실행하여 설치를 시작합니다.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>설치 프로그램을 실행할 때 Windows Defender SmartScreen이 앱 시작을 차단할 수 있습니다:</p>
            <img src="/tutorials/windows-install/1.png" alt="Windows Defender SmartScreen 경고" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>SmartScreen 경고에서 <strong>"추가 정보"</strong>를 클릭합니다</li>
              <li>창이 확장되어 더 많은 세부 정보가 표시됩니다:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Windows Defender SmartScreen 확장 보기" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li><strong>"실행"</strong>을 클릭하여 설치를 계속합니다</li>
            </ol>`,
          },
        ],
        warning:
          'Sokuji 설치 프로그램은 SignPath Foundation을 통해 코드 서명되어 있습니다. 그래도 SmartScreen 경고가 나타나면 위 단계를 따라 계속할 수 있습니다.',
      },
      {
        title: '단계 3: VB-CABLE 가상 오디오 장치 설치',
        content: `Sokuji는 다른 애플리케이션으로 오디오를 라우팅하기 위해 VB-CABLE 가상 오디오 장치를 필요로 합니다. 설치 프로그램이 자동 설치를 안내합니다.`,
        subsections: [
          {
            title: 'VB-CABLE 설치',
            content: `<p>SmartScreen을 우회한 후 VB-CABLE 설치 프롬프트가 표시됩니다:</p>
            <img src="/tutorials/windows-install/3.png" alt="VB-CABLE 설치 프롬프트" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li><strong>"Install Now"</strong>를 클릭하여 VB-CABLE을 자동으로 다운로드 및 설치합니다</li>
              <li>또는, <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a>에서 <em>Download Manually</em>로 직접 다운로드할 수 있습니다</li>
              <li>사용자 계정 컨트롤(UAC) 프롬프트가 나타나면 설치를 허용하기 위해 <strong>"예"</strong>를 클릭합니다:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="VB-CABLE에 대한 사용자 계정 컨트롤 프롬프트" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>VB-CABLE 설치 관리자가 열립니다. <strong>"Install Driver"</strong>를 클릭합니다:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="VB-CABLE 설치 관리자 창" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>설치가 완료될 때까지 기다립니다. 성공 메시지가 표시됩니다:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="VB-CABLE 설치 완료" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>설치 완료 대화상자에서 <strong>"OK"</strong>를 클릭합니다</li>
              <li>최종 확인이 표시됩니다:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="VB-CABLE 설치 성공 확인" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li><strong>"OK"</strong>를 클릭하여 VB-CABLE 설치를 마무리합니다</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE은 Sokuji가 번역된 오디오를 Zoom, Teams, Google Meet 등의 화상 회의 애플리케이션으로 전달할 수 있도록 하는 가상 오디오 장치를 생성합니다.',
      },
      {
        title: '단계 4: 첫 실행 설정',
        content: `VB-CABLE 설치 후 Sokuji가 자동으로 실행되며 오디오 구성 페이지가 표시됩니다:`,
        subsections: [
          {
            title: '오디오 장치 확인',
            content: `<img src="/tutorials/windows-install/7.png" alt="VB-CABLE 장치가 표시된 Sokuji 오디오 구성" class="install-page__screenshot" data-lightbox="true" />
            <p>VB-CABLE이 제대로 설치되었는지 확인하려면 <strong>Windows 소리 설정</strong>을 여세요(시스템 트레이의 스피커 아이콘을 마우스 오른쪽 버튼으로 클릭 → "소리 설정"):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - 녹음 장치에 표시되어야 합니다</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - 재생 장치에 표시되어야 합니다</li>
            </ul>
            <p><strong>참고:</strong> Sokuji의 장치 목록에는 이 CABLE 장치들이 표시되지 않으며, 이는 의도된 동작입니다. Sokuji는 의도치 않은 오디오 피드백 루프를 막기 위해 이 장치들을 숨기고, 백그라운드에서 VB-CABLE을 자동으로 감지해 사용합니다.</p>`,
          },
          {
            title: '설정 완료',
            content: `<ol>
              <li>선호하는 AI 공급자(OpenAI, Google Gemini 등) 구성</li>
              <li>선택한 공급자의 API 키 입력</li>
              <li>소스 언어와 대상 언어 선택</li>
              <li>오디오 입력/출력 장치 테스트</li>
              <li><strong>마이크</strong>에서 물리적 마이크 선택</li>
              <li><strong>음성</strong> 블록에서 <strong>기본 재생 장치</strong>를 스피커나 헤드폰으로 선택. <strong>나도 듣기</strong>와 <strong>내가 듣는 번역</strong>은 각각 다른 장치로 재생할 수 있으며, <strong>상대방에게 들리는 번역</strong>은 가상 마이크 CABLE Output (VB-Audio Virtual Cable)으로 보내집니다</li>
            </ol>`,
          },
        ],
        success:
          '이제 Sokuji를 사용할 준비가 되었습니다! 세션 시작 버튼을 클릭하여 실시간 번역을 시작할 수 있습니다.',
      },
    ],
    troubleshooting: {
      title: '문제 해결',
      issues: [
        {
          title: 'Windows Defender가 설치를 차단함',
          content: `Windows Defender SmartScreen이 설치를 차단하는 경우:
          <ol>
            <li>SmartScreen 경고에서 "추가 정보"를 클릭</li>
            <li>"실행"을 클릭하여 설치 계속</li>
          </ol>
          SmartScreen은 서명되었더라도 자주 보지 못한 설치 프로그램에 경고를 표시할 수 있습니다. 보안 문제를 뜻하는 것은 아닙니다.`,
        },
        {
          title: '마이크가 감지되지 않거나 열리지 않음',
          content: `세션을 시작할 때 마이크가 열리지 않으면 Sokuji가 이유를 알려 줍니다:
          <ul>
            <li><strong>"마이크 ‘…’을(를) …에서 사용 중입니다"</strong>: 다른 앱이 마이크를 사용하고 있습니다. 표시된 앱을 닫고 다시 시작하거나, <strong>설정</strong>을 클릭해 다른 마이크를 선택하세요.</li>
            <li><strong>"Windows에서 마이크 ‘…’을(를) 열 수 없습니다"</strong>: 다른 앱이 사용 중이거나 드라이버가 응답하지 않을 수 있습니다. 마이크를 사용하는 앱을 닫거나 마이크를 다시 연결한 뒤 다시 시작하세요. <strong>설정</strong>에서 다른 마이크를 선택할 수도 있습니다.</li>
            <li><strong>"Windows에서 Sokuji의 마이크 사용을 허용하지 않습니다"</strong>: Windows 개인 정보 설정이 데스크톱 앱의 마이크 사용을 막고 있습니다. <strong>시스템 설정 열기</strong>를 클릭해 데스크톱 앱의 마이크 액세스를 켠 뒤 다시 시작하세요.</li>
          </ul>
          마이크가 목록에 아예 보이지 않는 경우:
          <ol>
            <li>Windows 설정 → 개인 정보 및 보안 → 마이크 열기</li>
            <li>"마이크 액세스"와 "데스크톱 앱에서 마이크에 액세스하도록 허용"이 켜져 있는지 확인</li>
            <li>마이크가 올바르게 연결되고 기본 장치로 설정되어 있는지 확인</li>
          </ol>`,
        },
        {
          title: '오디오 출력 없음',
          content: `오디오가 들리지 않는 경우:
          <ol>
            <li>시스템 트레이의 스피커 아이콘을 마우스 오른쪽 버튼으로 클릭</li>
            <li>"사운드 설정 열기" 선택</li>
            <li>출력 장치가 올바르게 선택되었는지 확인</li>
            <li>볼륨 수준이 음소거되지 않았는지 확인</li>
            <li>Sokuji의 <strong>음성</strong> 블록에서 듣고 싶은 행의 장치 확인: 내 번역은 <strong>나도 듣기</strong>(기본값은 꺼짐), 상대방의 번역은 <strong>내가 듣는 번역</strong>, 그 밖의 소리는 <strong>기본 재생 장치</strong></li>
          </ol>`,
        },
        {
          title: '애플리케이션이 시작되지 않음',
          content: `Sokuji가 시작되지 않는 경우:
          <ul>
            <li>관리자 권한으로 실행 시도 (마우스 오른쪽 클릭 → 관리자 권한으로 실행)</li>
            <li>바이러스 백신 소프트웨어가 애플리케이션을 차단하는지 확인</li>
            <li>애플리케이션 재설치</li>
            <li>최신 Windows 업데이트가 설치되어 있는지 확인</li>
          </ul>`,
        },
        {
          title: 'VB-CABLE 장치가 감지되지 않음',
          content: `Sokuji에서 CABLE Output/Input 장치가 보이지 않는 경우:
          <ol>
            <li><strong>컴퓨터 재시작</strong> - 장치 인식 문제를 해결하는 데 도움이 됩니다</li>
            <li><strong>Windows 사운드 설정 확인</strong>:
              <ul>
                <li>시스템 트레이의 스피커 아이콘을 마우스 오른쪽 버튼으로 클릭</li>
                <li>"사운드 설정" 선택</li>
                <li>재생 장치에서 "CABLE Input" 확인</li>
                <li>녹음 장치에서 "CABLE Output" 확인</li>
              </ul>
            </li>
            <li><strong>VB-CABLE 수동 재설치</strong>:
              <ul>
                <li><a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a>에서 다운로드</li>
                <li>ZIP 파일 압축 해제</li>
                <li>관리자 권한으로 VBCABLE_Setup_x64.exe 실행</li>
                <li>설치 후 컴퓨터 재시작</li>
              </ul>
            </li>
            <li><strong>장치 관리자 확인</strong>:
              <ul>
                <li>장치 관리자 열기 (Win+X → 장치 관리자)</li>
                <li>"사운드, 비디오 및 게임 컨트롤러" 아래에서 확인</li>
                <li>"VB-Audio Virtual Cable"이 오류 없이 표시되는지 확인</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  es: {
    pageTitle: 'Guia de instalacion en Windows',
    overview:
      'Esta guia le ayudara a instalar Sokuji en sistemas Windows. Sokuji esta disponible como instalador .exe para una facil instalacion en Windows 10 y Windows 11.',
    downloadButton: 'Descargar para Windows',
    requirements: {
      title: 'Requisitos del sistema',
      items: [
        'Windows 10 (version 1903 o posterior) o Windows 11',
        'Procesador de 64 bits',
        '4GB de RAM minimo (8GB recomendado)',
        '200MB de espacio en disco disponible',
        'Conexion a internet para servicios de traduccion con IA',
        'Microfono y altavoces/auriculares',
      ],
    },
    steps: [
      {
        title: 'Paso 1: Descargar Sokuji',
        content: `Visite la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina oficial de lanzamientos en GitHub</a> para descargar la ultima version de Sokuji para Windows.`,
        subsections: [
          {
            title: 'Elegir el instalador correcto',
            content: `<ul>
              <li><strong>Instalador .exe</strong> - Instalador estandar para Windows</li>
              <li><strong>Version portatil</strong> - No requiere instalacion (si esta disponible)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Paso 2: Ejecutar el instalador',
        content: `Una vez descargado, localice el archivo del instalador y ejecutelo para comenzar la instalacion.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>Al ejecutar el instalador, Windows Defender SmartScreen puede impedir que la aplicacion se inicie:</p>
            <img src="/tutorials/windows-install/1.png" alt="Advertencia de Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Haga clic en <strong>"Mas informacion"</strong> en la advertencia de SmartScreen</li>
              <li>La ventana se expandira para mostrar mas detalles:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Vista expandida de Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Haga clic en <strong>"Ejecutar de todas formas"</strong> para continuar con la instalacion</li>
            </ol>`,
          },
        ],
        warning:
          'El instalador de Sokuji está firmado mediante SignPath Foundation. Si SmartScreen aun así muestra una advertencia, los pasos anteriores le permiten continuar.',
      },
      {
        title: 'Paso 3: Instalar el dispositivo de audio virtual VB-CABLE',
        content: `Sokuji requiere el dispositivo de audio virtual VB-CABLE para enrutar el audio a otras aplicaciones. El instalador le pedira que lo instale automaticamente.`,
        subsections: [
          {
            title: 'Instalacion de VB-CABLE',
            content: `<p>Despues de omitir SmartScreen, vera el mensaje de instalacion de VB-CABLE:</p>
            <img src="/tutorials/windows-install/3.png" alt="Mensaje de instalacion de VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Haga clic en <strong>"Install Now"</strong> para descargar e instalar VB-CABLE automaticamente</li>
              <li>Alternativamente, puede hacer clic en "Download Manually" para obtenerlo desde <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>Cuando aparezca el mensaje de Control de cuentas de usuario, haga clic en <strong>"Si"</strong> para permitir la instalacion:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Mensaje de Control de cuentas de usuario para VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>El instalador de VB-CABLE se abrira. Haga clic en <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="Ventana del instalador de VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Espere a que se complete la instalacion. Vera un mensaje de exito:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="Instalacion de VB-CABLE completada" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Haga clic en <strong>"OK"</strong> en el dialogo de instalacion completada</li>
              <li>Aparecera la confirmacion final:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Confirmacion de exito de instalacion de VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Haga clic en <strong>"OK"</strong> para finalizar la instalacion de VB-CABLE</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE crea dispositivos de audio virtuales que permiten a Sokuji pasar el audio traducido a aplicaciones de videoconferencia como Zoom, Teams o Google Meet.',
      },
      {
        title: 'Paso 4: Configuracion del primer inicio',
        content: `Despues de la instalacion de VB-CABLE, Sokuji se iniciara automaticamente y vera la pagina de configuracion de audio:`,
        subsections: [
          {
            title: 'Verificar dispositivos de audio',
            content: `<img src="/tutorials/windows-install/7.png" alt="Configuracion de audio de Sokuji mostrando dispositivos VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>Para comprobar que VB-CABLE se instaló correctamente, abra la <strong>configuración de sonido de Windows</strong> (clic derecho en el icono del altavoz de la bandeja del sistema → "Configuración de sonido"):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - Debe aparecer en los dispositivos de grabación</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - Debe aparecer en los dispositivos de reproducción</li>
            </ul>
            <p><strong>Nota:</strong> Estos dispositivos CABLE no aparecen en la lista de dispositivos de Sokuji, y es intencionado. Sokuji los oculta para evitar bucles de realimentación de audio accidentales, y detecta y usa VB-CABLE automáticamente en segundo plano.</p>`,
          },
          {
            title: 'Completar la configuracion',
            content: `<ol>
              <li>Configure su proveedor de IA preferido (OpenAI, Google Gemini, etc.)</li>
              <li>Ingrese su clave API para el proveedor seleccionado</li>
              <li>Seleccione los idiomas de origen y destino</li>
              <li>Pruebe los dispositivos de entrada/salida de audio</li>
              <li>Seleccione su micrófono físico en <strong>Micrófono</strong></li>
              <li>En el bloque <strong>Voz</strong>, elija sus altavoces o auriculares como <strong>Dispositivo de reproducción predeterminado</strong>. <strong>Yo también la oigo</strong> y <strong>Traducción que yo oigo</strong> pueden sonar cada una en su propio dispositivo; <strong>Traducción que oye la otra parte</strong> va al micrófono virtual, CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji esta listo para usar. Puede iniciar la traduccion en tiempo real haciendo clic en el boton de inicio de sesion.',
      },
    ],
    troubleshooting: {
      title: 'Solucion de problemas',
      issues: [
        {
          title: 'Windows Defender bloquea la instalacion',
          content: `Si Windows Defender SmartScreen bloquea la instalacion:
          <ol>
            <li>Haga clic en "Mas informacion" en la advertencia de SmartScreen</li>
            <li>Haga clic en "Ejecutar de todas formas" para continuar con la instalacion</li>
          </ol>
          SmartScreen puede advertir sobre un instalador firmado que ha visto pocas veces. Esto no indica un problema de seguridad.`,
        },
        {
          title: 'Micrófono no detectado o no se abre',
          content: `Si el micrófono no se abre al iniciar, Sokuji le indica el motivo:
          <ul>
            <li><strong>"El micrófono «…» está ocupado por …"</strong>: otra aplicación lo está usando. Cierre la aplicación indicada y vuelva a empezar, o haga clic en <strong>Configuración</strong> y elija otro micrófono.</li>
            <li><strong>"Windows no pudo abrir el micrófono «…»"</strong>: puede que otra aplicación lo esté usando o que su controlador haya dejado de responder. Cierre las aplicaciones que lo usan o vuelva a conectarlo y vuelva a empezar; en <strong>Configuración</strong> también puede elegir otro micrófono.</li>
            <li><strong>"Windows no permite que Sokuji use el micrófono"</strong>: la configuración de privacidad de Windows impide que las aplicaciones de escritorio usen el micrófono. Haga clic en <strong>Abrir Ajustes del Sistema</strong>, active el acceso al micrófono para las aplicaciones de escritorio y vuelva a empezar.</li>
          </ul>
          Si su micrófono no aparece en la lista:
          <ol>
            <li>Abra Configuración de Windows → Privacidad y seguridad → Micrófono</li>
            <li>Asegúrese de que "Acceso al micrófono" y "Permitir que las aplicaciones de escritorio accedan al micrófono" estén activados</li>
            <li>Verifique que su micrófono esté correctamente conectado y configurado como dispositivo predeterminado</li>
          </ol>`,
        },
        {
          title: 'Sin salida de audio',
          content: `Si no escucha ningun audio:
          <ol>
            <li>Haga clic derecho en el icono del altavoz en la bandeja del sistema</li>
            <li>Seleccione "Abrir configuracion de sonido"</li>
            <li>Verifique que su dispositivo de salida este correctamente seleccionado</li>
            <li>Compruebe que los niveles de volumen no esten silenciados</li>
            <li>En el bloque <strong>Voz</strong> de Sokuji, revise el dispositivo de la fila que espera oír: <strong>Yo también la oigo</strong> para su propia traducción (desactivada por defecto), <strong>Traducción que yo oigo</strong> para la de la otra parte y <strong>Dispositivo de reproducción predeterminado</strong> para todo lo demás</li>
          </ol>`,
        },
        {
          title: 'La aplicacion no se inicia',
          content: `Si Sokuji no se inicia:
          <ul>
            <li>Intente ejecutar como Administrador (clic derecho → Ejecutar como administrador)</li>
            <li>Verifique si el software antivirus esta bloqueando la aplicacion</li>
            <li>Reinstale la aplicacion</li>
            <li>Asegurese de tener instaladas las ultimas actualizaciones de Windows</li>
          </ul>`,
        },
        {
          title: 'Dispositivos VB-CABLE no detectados',
          content: `Si no ve los dispositivos CABLE Output/Input en Sokuji:
          <ol>
            <li><strong>Reinicie su computadora</strong> - Esto a menudo resuelve problemas de reconocimiento de dispositivos</li>
            <li><strong>Verifique la Configuracion de Sonido de Windows</strong>:
              <ul>
                <li>Haga clic derecho en el icono del altavoz en la bandeja del sistema</li>
                <li>Seleccione "Configuracion de sonido"</li>
                <li>Busque "CABLE Input" en los dispositivos de reproduccion</li>
                <li>Busque "CABLE Output" en los dispositivos de grabacion</li>
              </ul>
            </li>
            <li><strong>Reinstale VB-CABLE manualmente</strong>:
              <ul>
                <li>Descargue desde <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Extraiga el archivo ZIP</li>
                <li>Ejecute VBCABLE_Setup_x64.exe como Administrador</li>
                <li>Reinicie su computadora despues de la instalacion</li>
              </ul>
            </li>
            <li><strong>Verifique el Administrador de dispositivos</strong>:
              <ul>
                <li>Abra el Administrador de dispositivos (Win+X → Administrador de dispositivos)</li>
                <li>Busque en "Controladores de sonido, video y juegos"</li>
                <li>Verifique que "VB-Audio Virtual Cable" este listado sin errores</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  fr: {
    pageTitle: "Guide d'installation Windows",
    overview:
      "Ce guide vous accompagnera dans l'installation de Sokuji sur les systemes Windows. Sokuji est disponible sous forme d'installateur .exe pour une installation facile sur Windows 10 et Windows 11.",
    downloadButton: 'Telecharger pour Windows',
    requirements: {
      title: 'Configuration requise',
      items: [
        'Windows 10 (version 1903 ou ulterieure) ou Windows 11',
        'Processeur 64 bits',
        '4 Go de RAM minimum (8 Go recommandes)',
        '200 Mo d\'espace disque disponible',
        'Connexion Internet pour les services de traduction IA',
        'Microphone et haut-parleurs/ecouteurs',
      ],
    },
    steps: [
      {
        title: 'Etape 1 : Telecharger Sokuji',
        content: `Visitez la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">page officielle des versions sur GitHub</a> pour telecharger la derniere version de Sokuji pour Windows.`,
        subsections: [
          {
            title: "Choisir le bon installateur",
            content: `<ul>
              <li><strong>Installateur .exe</strong> - Installateur standard pour Windows</li>
              <li><strong>Version portable</strong> - Aucune installation requise (si disponible)</li>
            </ul>`,
          },
        ],
      },
      {
        title: "Etape 2 : Executer l'installateur",
        content: `Une fois telecharge, localisez le fichier d'installation et executez-le pour commencer l'installation.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>Lorsque vous executez l'installateur, Windows Defender SmartScreen peut empecher l'application de demarrer :</p>
            <img src="/tutorials/windows-install/1.png" alt="Avertissement Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Cliquez sur <strong>"Informations supplementaires"</strong> sur l'avertissement SmartScreen</li>
              <li>La fenetre s'agrandira pour afficher plus de details :</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Vue etendue de Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Cliquez sur <strong>"Executer quand meme"</strong> pour poursuivre l'installation</li>
            </ol>`,
          },
        ],
        warning:
          "L'installateur de Sokuji est signé via SignPath Foundation. Si SmartScreen affiche quand même un avertissement, les étapes ci-dessus permettent de continuer.",
      },
      {
        title: "Etape 3 : Installer le peripherique audio virtuel VB-CABLE",
        content: `Sokuji necessite le peripherique audio virtuel VB-CABLE pour acheminer l'audio vers d'autres applications. L'installateur vous invitera a l'installer automatiquement.`,
        subsections: [
          {
            title: 'Installation de VB-CABLE',
            content: `<p>Apres avoir contourne SmartScreen, vous verrez l'invite d'installation de VB-CABLE :</p>
            <img src="/tutorials/windows-install/3.png" alt="Invite d'installation de VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Cliquez sur <strong>"Install Now"</strong> pour telecharger et installer VB-CABLE automatiquement</li>
              <li>Vous pouvez egalement cliquer sur "Download Manually" pour l'obtenir depuis <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>Lorsque l'invite de Controle de compte d'utilisateur apparait, cliquez sur <strong>"Oui"</strong> pour autoriser l'installation :</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Invite de Controle de compte d'utilisateur pour VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>L'installateur VB-CABLE s'ouvrira. Cliquez sur <strong>"Install Driver"</strong> :</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="Fenetre de l'installateur VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Attendez que l'installation se termine. Vous verrez un message de succes :</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="Installation de VB-CABLE terminee" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Cliquez sur <strong>"OK"</strong> dans la boite de dialogue d'installation terminee</li>
              <li>La confirmation finale apparaitra :</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Confirmation de succes de l'installation VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Cliquez sur <strong>"OK"</strong> pour terminer l'installation de VB-CABLE</li>
            </ol>`,
          },
        ],
        info: "VB-CABLE cree des peripheriques audio virtuels qui permettent a Sokuji de transmettre l'audio traduit aux applications de videoconference comme Zoom, Teams ou Google Meet.",
      },
      {
        title: 'Etape 4 : Configuration du premier lancement',
        content: `Apres l'installation de VB-CABLE, Sokuji se lancera automatiquement et vous verrez la page de configuration audio :`,
        subsections: [
          {
            title: 'Verifier les peripheriques audio',
            content: `<img src="/tutorials/windows-install/7.png" alt="Configuration audio de Sokuji affichant les peripheriques VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>Pour vérifier que VB-CABLE est bien installé, ouvrez les <strong>paramètres de son de Windows</strong> (clic droit sur l'icône du haut-parleur dans la zone de notification → « Paramètres du son ») :</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - Doit apparaître dans les périphériques d'enregistrement</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - Doit apparaître dans les périphériques de lecture</li>
            </ul>
            <p><strong>Remarque :</strong> ces périphériques CABLE n'apparaissent pas dans la liste des périphériques de Sokuji, et c'est voulu. Sokuji les masque pour éviter les boucles de retour audio accidentelles, et détecte et utilise VB-CABLE automatiquement en arrière-plan.</p>`,
          },
          {
            title: 'Terminer la configuration',
            content: `<ol>
              <li>Configurez votre fournisseur d'IA prefere (OpenAI, Google Gemini, etc.)</li>
              <li>Entrez votre cle API pour le fournisseur selectionne</li>
              <li>Selectionnez les langues source et cible</li>
              <li>Testez les peripheriques d'entree/sortie audio</li>
              <li>Sélectionnez votre microphone physique dans <strong>Microphone</strong></li>
              <li>Dans le bloc <strong>Voix</strong>, choisissez vos haut-parleurs ou votre casque comme <strong>Appareil de lecture par défaut</strong>. <strong>Je l'entends aussi</strong> et <strong>Traduction que j'entends</strong> peuvent chacun jouer sur leur propre appareil ; <strong>Traduction entendue par l'autre</strong> part dans le micro virtuel, CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          "Sokuji est maintenant pret a l'emploi ! Vous pouvez demarrer la traduction en temps reel en cliquant sur le bouton de demarrage de session.",
      },
    ],
    troubleshooting: {
      title: 'Depannage',
      issues: [
        {
          title: "Windows Defender bloque l'installation",
          content: `Si Windows Defender SmartScreen bloque l'installation :
          <ol>
            <li>Cliquez sur "Informations supplementaires" sur l'avertissement SmartScreen</li>
            <li>Cliquez sur "Executer quand meme" pour poursuivre l'installation</li>
          </ol>
          SmartScreen peut avertir au sujet d'un installateur signé qu'il a rarement vu. Cela n'indique pas un problème de sécurité.`,
        },
        {
          title: 'Microphone non détecté ou impossible à ouvrir',
          content: `Si le microphone ne s'ouvre pas au démarrage, Sokuji en indique la raison :
          <ul>
            <li><strong>"Le microphone « … » est utilisé par …"</strong> : une autre application l'utilise. Fermez l'application indiquée puis relancez, ou cliquez sur <strong>Paramètres</strong> et choisissez un autre microphone.</li>
            <li><strong>"Windows n’a pas pu ouvrir le microphone « … »"</strong> : une autre application l'utilise peut-être, ou son pilote ne répond plus. Fermez les applications qui l'utilisent ou reconnectez-le, puis relancez ; <strong>Paramètres</strong> permet aussi de choisir un autre microphone.</li>
            <li><strong>"Windows n’autorise pas Sokuji à utiliser le microphone"</strong> : les paramètres de confidentialité de Windows interdisent le microphone aux applications de bureau. Cliquez sur <strong>Ouvrir les Réglages Système</strong>, activez l'accès au microphone pour les applications de bureau, puis relancez.</li>
          </ul>
          Si votre microphone n'apparaît pas du tout dans la liste :
          <ol>
            <li>Ouvrez Paramètres Windows → Confidentialité et sécurité → Microphone</li>
            <li>Assurez-vous que "Accès au microphone" et "Autoriser les applications de bureau à accéder à votre microphone" sont activés</li>
            <li>Vérifiez que votre microphone est correctement connecté et défini comme périphérique par défaut</li>
          </ol>`,
        },
        {
          title: 'Pas de sortie audio',
          content: `Si vous n'entendez aucun audio :
          <ol>
            <li>Faites un clic droit sur l'icone du haut-parleur dans la barre des taches</li>
            <li>Selectionnez "Ouvrir les parametres de son"</li>
            <li>Verifiez que votre peripherique de sortie est correctement selectionne</li>
            <li>Verifiez que les niveaux de volume ne sont pas coupes</li>
            <li>Dans le bloc <strong>Voix</strong> de Sokuji, vérifiez l'appareil de la ligne que vous voulez entendre : <strong>Je l'entends aussi</strong> pour votre propre traduction (désactivé par défaut), <strong>Traduction que j'entends</strong> pour celle de l'autre, et <strong>Appareil de lecture par défaut</strong> pour tout le reste</li>
          </ol>`,
        },
        {
          title: "L'application ne demarre pas",
          content: `Si Sokuji ne demarre pas :
          <ul>
            <li>Essayez d'executer en tant qu'Administrateur (clic droit → Executer en tant qu'administrateur)</li>
            <li>Verifiez si un logiciel antivirus bloque l'application</li>
            <li>Reinstallez l'application</li>
            <li>Assurez-vous d'avoir installe les dernieres mises a jour Windows</li>
          </ul>`,
        },
        {
          title: 'Peripheriques VB-CABLE non detectes',
          content: `Si vous ne voyez pas les peripheriques CABLE Output/Input dans Sokuji :
          <ol>
            <li><strong>Redemarrez votre ordinateur</strong> - Cela resout souvent les problemes de reconnaissance des peripheriques</li>
            <li><strong>Verifiez les Parametres de Son Windows</strong> :
              <ul>
                <li>Faites un clic droit sur l'icone du haut-parleur dans la barre des taches</li>
                <li>Selectionnez "Parametres de son"</li>
                <li>Recherchez "CABLE Input" dans les peripheriques de lecture</li>
                <li>Recherchez "CABLE Output" dans les peripheriques d'enregistrement</li>
              </ul>
            </li>
            <li><strong>Reinstallez VB-CABLE manuellement</strong> :
              <ul>
                <li>Telechargez depuis <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Extrayez le fichier ZIP</li>
                <li>Executez VBCABLE_Setup_x64.exe en tant qu'Administrateur</li>
                <li>Redemarrez votre ordinateur apres l'installation</li>
              </ul>
            </li>
            <li><strong>Verifiez le Gestionnaire de peripheriques</strong> :
              <ul>
                <li>Ouvrez le Gestionnaire de peripheriques (Win+X → Gestionnaire de peripheriques)</li>
                <li>Regardez sous "Controleurs audio, video et jeu"</li>
                <li>Verifiez que "VB-Audio Virtual Cable" est liste sans erreurs</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  ar: {
    pageTitle: 'دليل تثبيت Windows',
    overview:
      'سيرشدك هذا الدليل خلال تثبيت Sokuji على انظمة Windows. يتوفر Sokuji كمثبت .exe للتثبيت السهل على Windows 10 و Windows 11.',
    downloadButton: 'تحميل لنظام Windows',
    requirements: {
      title: 'متطلبات النظام',
      items: [
        'Windows 10 (الاصدار 1903 او احدث) او Windows 11',
        'معالج 64 بت',
        '4 جيجابايت رام كحد ادنى (8 جيجابايت موصى بها)',
        '200 ميجابايت مساحة قرص متاحة',
        'اتصال بالانترنت لخدمات الترجمة بالذكاء الاصطناعي',
        'ميكروفون ومكبرات صوت/سماعات',
      ],
    },
    steps: [
      {
        title: 'الخطوة 1: تحميل Sokuji',
        content: `قم بزيارة <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">صفحة الاصدارات الرسمية على GitHub</a> لتحميل احدث اصدار من Sokuji لنظام Windows.`,
        subsections: [
          {
            title: 'اختيار المثبت الصحيح',
            content: `<ul>
              <li><strong>مثبت .exe</strong> - المثبت القياسي لنظام Windows</li>
              <li><strong>النسخة المحمولة</strong> - لا يتطلب تثبيتا (اذا كان متاحا)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'الخطوة 2: تشغيل المثبت',
        content: `بعد التحميل، حدد موقع ملف المثبت وقم بتشغيله لبدء التثبيت.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>عند تشغيل المثبت، قد يمنع Windows Defender SmartScreen التطبيق من البدء:</p>
            <img src="/tutorials/windows-install/1.png" alt="تحذير Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>انقر على <strong>"مزيد من المعلومات"</strong> في تحذير SmartScreen</li>
              <li>ستتوسع النافذة لعرض مزيد من التفاصيل:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="عرض موسع لـ Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>انقر على <strong>"التشغيل على اي حال"</strong> للمتابعة مع التثبيت</li>
            </ol>`,
          },
        ],
        warning:
          'مثبت Sokuji موقّع رقميا عبر مؤسسة SignPath. إذا ظل SmartScreen يعرض تحذيرا، فاتبع الخطوات أعلاه للمتابعة.',
      },
      {
        title: 'الخطوة 3: تثبيت جهاز الصوت الافتراضي VB-CABLE',
        content: `يتطلب Sokuji جهاز الصوت الافتراضي VB-CABLE لتوجيه الصوت الى التطبيقات الاخرى. سيطلب منك المثبت تثبيته تلقائيا.`,
        subsections: [
          {
            title: 'تثبيت VB-CABLE',
            content: `<p>بعد تجاوز SmartScreen، سترى رسالة تثبيت VB-CABLE:</p>
            <img src="/tutorials/windows-install/3.png" alt="رسالة تثبيت VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>انقر على <strong>"Install Now"</strong> لتحميل وتثبيت VB-CABLE تلقائيا</li>
              <li>بدلا من ذلك، يمكنك النقر على "Download Manually" للحصول عليه من <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>عند ظهور رسالة التحكم في حساب المستخدم، انقر على <strong>"نعم"</strong> للسماح بالتثبيت:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="رسالة التحكم في حساب المستخدم لـ VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>سيفتح مثبت VB-CABLE. انقر على <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="نافذة مثبت VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>انتظر حتى يكتمل التثبيت. ستظهر رسالة نجاح:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="اكتمال تثبيت VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>انقر على <strong>"OK"</strong> في مربع حوار اكتمال التثبيت</li>
              <li>سيظهر التاكيد النهائي:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="تاكيد نجاح تثبيت VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>انقر على <strong>"OK"</strong> لانهاء تثبيت VB-CABLE</li>
            </ol>`,
          },
        ],
        info: 'ينشئ VB-CABLE اجهزة صوت افتراضية تسمح لـ Sokuji بتمرير الصوت المترجم الى تطبيقات مؤتمرات الفيديو مثل Zoom و Teams و Google Meet.',
      },
      {
        title: 'الخطوة 4: اعداد التشغيل الاول',
        content: `بعد تثبيت VB-CABLE، سيتم تشغيل Sokuji تلقائيا وسترى صفحة تكوين الصوت:`,
        subsections: [
          {
            title: 'التحقق من اجهزة الصوت',
            content: `<img src="/tutorials/windows-install/7.png" alt="تكوين صوت Sokuji يعرض اجهزة VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>للتحقق من تثبيت VB-CABLE بنجاح، افتح <strong>إعدادات الصوت في Windows</strong> (انقر بزر الماوس الأيمن على أيقونة مكبر الصوت في علبة النظام ← "إعدادات الصوت"):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - يجب أن يظهر ضمن أجهزة التسجيل</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - يجب أن يظهر ضمن أجهزة التشغيل</li>
            </ul>
            <p><strong>ملاحظة:</strong> لن ترى أجهزة CABLE هذه في قائمة أجهزة Sokuji، وهذا مقصود. يخفيها Sokuji لمنع حلقات ارتداد الصوت غير المقصودة، ويكتشف VB-CABLE ويستخدمه تلقائيًا في الخلفية.</p>`,
          },
          {
            title: 'اكمال الاعداد',
            content: `<ol>
              <li>قم بتكوين مزود الذكاء الاصطناعي المفضل لديك (OpenAI، Google Gemini، الخ.)</li>
              <li>ادخل مفتاح API للمزود المحدد</li>
              <li>حدد لغات المصدر والهدف</li>
              <li>اختبر اجهزة ادخال/اخراج الصوت</li>
              <li>حدد الميكروفون الفعلي في <strong>ميكروفون</strong></li>
              <li>في قسم <strong>الكلام</strong>، اختر مكبرات الصوت أو السماعات كـ <strong>جهاز التشغيل الافتراضي</strong>. يمكن لكل من <strong>أسمعها أنا أيضًا</strong> و<strong>الترجمة التي أسمعها</strong> أن يعمل على جهازه الخاص؛ أما <strong>الترجمة التي يسمعها الطرف الآخر</strong> فتذهب إلى الميكروفون الافتراضي <code dir="ltr">CABLE Output (VB-Audio Virtual Cable)</code></li>
            </ol>`,
          },
        ],
        success:
          'Sokuji جاهز الآن للاستخدام! يمكنك بدء الترجمة في الوقت الفعلي بالنقر على زر بدء الجلسة.',
      },
    ],
    troubleshooting: {
      title: 'استكشاف الاخطاء واصلاحها',
      issues: [
        {
          title: 'Windows Defender يمنع التثبيت',
          content: `اذا منع Windows Defender SmartScreen التثبيت:
          <ol>
            <li>انقر على "مزيد من المعلومات" في تحذير SmartScreen</li>
            <li>انقر على "التشغيل على اي حال" للمتابعة مع التثبيت</li>
          </ol>
          قد يحذّر SmartScreen من مثبت موقّع لم يره كثيرا. وهذا لا يشير إلى مشكلة أمنية.`,
        },
        {
          title: 'لم يتم الكشف عن الميكروفون أو تعذّر فتحه',
          content: `إذا تعذّر فتح الميكروفون عند البدء، يوضح Sokuji السبب:
          <ul>
            <li><strong>"الميكروفون «…» قيد الاستخدام بواسطة …"</strong>: تطبيق آخر يستخدمه. أغلق التطبيق المذكور وابدأ من جديد، أو انقر على <strong>الإعدادات</strong> واختر ميكروفونًا آخر.</li>
            <li><strong>"تعذّر على Windows فتح الميكروفون «…»"</strong>: ربما يستخدمه تطبيق آخر، أو توقف برنامج التشغيل الخاص به عن الاستجابة. أغلق التطبيقات التي تستخدمه أو أعد توصيله، ثم ابدأ من جديد؛ ويمكنك أيضًا اختيار ميكروفون آخر من <strong>الإعدادات</strong>.</li>
            <li><strong>"لا يسمح Windows لـ Sokuji باستخدام الميكروفون"</strong>: إعدادات الخصوصية في Windows تمنع تطبيقات سطح المكتب من استخدام الميكروفون. انقر على <strong>فتح إعدادات النظام</strong>، وفعّل وصول تطبيقات سطح المكتب إلى الميكروفون، ثم ابدأ من جديد.</li>
          </ul>
          إذا لم يظهر الميكروفون في القائمة إطلاقًا:
          <ol>
            <li>افتح إعدادات Windows ← الخصوصية والأمان ← الميكروفون</li>
            <li>تأكد من تشغيل "الوصول إلى الميكروفون" و"السماح لتطبيقات سطح المكتب بالوصول إلى الميكروفون"</li>
            <li>تحقق من ان الميكروفون متصل بشكل صحيح ومعين كجهاز افتراضي</li>
          </ol>`,
        },
        {
          title: 'لا يوجد اخراج صوتي',
          content: `اذا لم تسمع اي صوت:
          <ol>
            <li>انقر بزر الماوس الايمن على ايقونة مكبر الصوت في شريط المهام</li>
            <li>حدد "فتح اعدادات الصوت"</li>
            <li>تحقق من ان جهاز الاخراج محدد بشكل صحيح</li>
            <li>تحقق من ان مستويات الصوت ليست مكتومة</li>
            <li>في قسم <strong>الكلام</strong> في Sokuji، تحقق من جهاز الصف الذي تتوقع سماعه: <strong>أسمعها أنا أيضًا</strong> لترجمتك أنت (متوقف افتراضيًا)، و<strong>الترجمة التي أسمعها</strong> لترجمة الطرف الآخر، و<strong>جهاز التشغيل الافتراضي</strong> لكل ما عدا ذلك</li>
          </ol>`,
        },
        {
          title: 'التطبيق لا يبدا',
          content: `اذا لم يبدا Sokuji:
          <ul>
            <li>حاول التشغيل كمسؤول (انقر بزر الماوس الايمن ← التشغيل كمسؤول)</li>
            <li>تحقق مما اذا كان برنامج مكافحة الفيروسات يحظر التطبيق</li>
            <li>اعد تثبيت التطبيق</li>
            <li>تاكد من تثبيت احدث تحديثات Windows</li>
          </ul>`,
        },
        {
          title: 'لم يتم الكشف عن اجهزة VB-CABLE',
          content: `اذا لم ترَ اجهزة CABLE Output/Input في Sokuji:
          <ol>
            <li><strong>اعد تشغيل جهاز الكمبيوتر</strong> - هذا غالبا ما يحل مشاكل التعرف على الاجهزة</li>
            <li><strong>تحقق من اعدادات الصوت في Windows</strong>:
              <ul>
                <li>انقر بزر الماوس الايمن على ايقونة مكبر الصوت في شريط المهام</li>
                <li>حدد "اعدادات الصوت"</li>
                <li>ابحث عن "CABLE Input" في اجهزة التشغيل</li>
                <li>ابحث عن "CABLE Output" في اجهزة التسجيل</li>
              </ul>
            </li>
            <li><strong>اعد تثبيت VB-CABLE يدويا</strong>:
              <ul>
                <li>حمل من <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>استخرج ملف ZIP</li>
                <li>شغل VBCABLE_Setup_x64.exe كمسؤول</li>
                <li>اعد تشغيل الكمبيوتر بعد التثبيت</li>
              </ul>
            </li>
            <li><strong>تحقق من ادارة الاجهزة</strong>:
              <ul>
                <li>افتح ادارة الاجهزة (Win+X ← ادارة الاجهزة)</li>
                <li>ابحث تحت "اجهزة التحكم بالصوت والفيديو والالعاب"</li>
                <li>تحقق من ان "VB-Audio Virtual Cable" مدرج بدون اخطاء</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  pt: {
    pageTitle: 'Guia de Instalacao no Windows',
    overview:
      'Este guia orientara voce na instalacao do Sokuji em sistemas Windows. O Sokuji esta disponivel como instalador .exe para facil instalacao no Windows 10 e Windows 11.',
    downloadButton: 'Baixar para Windows',
    requirements: {
      title: 'Requisitos do Sistema',
      items: [
        'Windows 10 (versao 1903 ou posterior) ou Windows 11',
        'Processador de 64 bits',
        '4GB de RAM minimo (8GB recomendado)',
        '200MB de espaco em disco disponivel',
        'Conexao com a internet para servicos de traducao com IA',
        'Microfone e alto-falantes/fones de ouvido',
      ],
    },
    steps: [
      {
        title: 'Passo 1: Baixar o Sokuji',
        content: `Visite a <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina oficial de lancamentos no GitHub</a> para baixar a versao mais recente do Sokuji para Windows.`,
        subsections: [
          {
            title: 'Escolher o instalador correto',
            content: `<ul>
              <li><strong>Instalador .exe</strong> - Instalador padrao para Windows</li>
              <li><strong>Versao portatil</strong> - Nao requer instalacao (se disponivel)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Passo 2: Executar o instalador',
        content: `Apos o download, localize o arquivo do instalador e execute-o para iniciar a instalacao.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>Ao executar o instalador, o Windows Defender SmartScreen pode impedir o inicio do aplicativo:</p>
            <img src="/tutorials/windows-install/1.png" alt="Aviso do Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Clique em <strong>"Mais informacoes"</strong> no aviso do SmartScreen</li>
              <li>A janela se expandira para mostrar mais detalhes:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Visualizacao expandida do Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Clique em <strong>"Executar mesmo assim"</strong> para prosseguir com a instalacao</li>
            </ol>`,
          },
        ],
        warning:
          'O instalador do Sokuji é assinado por meio da SignPath Foundation. Se o SmartScreen ainda mostrar um aviso, os passos acima permitem continuar.',
      },
      {
        title: 'Passo 3: Instalar o dispositivo de audio virtual VB-CABLE',
        content: `O Sokuji requer o dispositivo de audio virtual VB-CABLE para rotear o audio para outros aplicativos. O instalador solicitara a instalacao automatica.`,
        subsections: [
          {
            title: 'Instalacao do VB-CABLE',
            content: `<p>Apos ignorar o SmartScreen, voce vera o prompt de instalacao do VB-CABLE:</p>
            <img src="/tutorials/windows-install/3.png" alt="Prompt de instalacao do VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Clique em <strong>"Install Now"</strong> para baixar e instalar o VB-CABLE automaticamente</li>
              <li>Alternativamente, voce pode clicar em "Download Manually" para obte-lo em <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>Quando o prompt de Controle de Conta de Usuario aparecer, clique em <strong>"Sim"</strong> para permitir a instalacao:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Prompt de Controle de Conta de Usuario para VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>O instalador do VB-CABLE sera aberto. Clique em <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="Janela do instalador do VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Aguarde a conclusao da instalacao. Voce vera uma mensagem de sucesso:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="Instalacao do VB-CABLE concluida" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Clique em <strong>"OK"</strong> na caixa de dialogo de instalacao concluida</li>
              <li>A confirmacao final aparecera:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Confirmacao de sucesso da instalacao do VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Clique em <strong>"OK"</strong> para finalizar a instalacao do VB-CABLE</li>
            </ol>`,
          },
        ],
        info: 'O VB-CABLE cria dispositivos de audio virtuais que permitem ao Sokuji transmitir o audio traduzido para aplicativos de videoconferencia como Zoom, Teams ou Google Meet.',
      },
      {
        title: 'Passo 4: Configuracao da primeira execucao',
        content: `Apos a instalacao do VB-CABLE, o Sokuji sera iniciado automaticamente e voce vera a pagina de configuracao de audio:`,
        subsections: [
          {
            title: 'Verificar dispositivos de audio',
            content: `<img src="/tutorials/windows-install/7.png" alt="Configuracao de audio do Sokuji mostrando dispositivos VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>Para verificar se o VB-CABLE foi instalado corretamente, abra as <strong>Configurações de som do Windows</strong> (clique com o botão direito no ícone do alto-falante na bandeja do sistema → "Configurações de som"):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - Deve aparecer nos dispositivos de gravação</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - Deve aparecer nos dispositivos de reprodução</li>
            </ul>
            <p><strong>Observação:</strong> você não verá esses dispositivos CABLE na lista de dispositivos do Sokuji, e isso é intencional. O Sokuji os oculta para evitar loops de retorno de áudio acidentais e detecta e usa o VB-CABLE automaticamente em segundo plano.</p>`,
          },
          {
            title: 'Concluir a configuracao',
            content: `<ol>
              <li>Configure seu provedor de IA preferido (OpenAI, Google Gemini, etc.)</li>
              <li>Insira sua chave API para o provedor selecionado</li>
              <li>Selecione os idiomas de origem e destino</li>
              <li>Teste os dispositivos de entrada/saida de audio</li>
              <li>Selecione seu microfone físico em <strong>Microfone</strong></li>
              <li>No bloco <strong>Voz</strong>, escolha seus alto-falantes ou fones de ouvido como <strong>Dispositivo de reprodução padrão</strong>. <strong>Eu também ouço</strong> e <strong>Tradução que eu ouço</strong> podem tocar cada um em seu próprio dispositivo; <strong>Tradução que o outro lado ouve</strong> vai para o microfone virtual, CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'O Sokuji esta pronto para uso! Voce pode iniciar a traducao em tempo real clicando no botao de inicio de sessao.',
      },
    ],
    troubleshooting: {
      title: 'Solucao de Problemas',
      issues: [
        {
          title: 'Windows Defender bloqueia a instalacao',
          content: `Se o Windows Defender SmartScreen bloquear a instalacao:
          <ol>
            <li>Clique em "Mais informacoes" no aviso do SmartScreen</li>
            <li>Clique em "Executar mesmo assim" para prosseguir com a instalacao</li>
          </ol>
          O SmartScreen pode avisar sobre um instalador assinado que viu poucas vezes. Isso não indica um problema de segurança.`,
        },
        {
          title: 'Microfone não detectado ou não abre',
          content: `Se o microfone não abrir ao iniciar, o Sokuji informa o motivo:
          <ul>
            <li><strong>"O microfone “…” está sendo usado por …"</strong>: outro aplicativo está usando o microfone. Feche o aplicativo indicado e inicie novamente, ou clique em <strong>Configurações</strong> e escolha outro microfone.</li>
            <li><strong>"O Windows não conseguiu abrir o microfone “…”"</strong>: outro aplicativo pode estar usando-o, ou o driver dele parou de responder. Feche os aplicativos que o usam ou reconecte-o e inicie novamente; em <strong>Configurações</strong> você também pode escolher outro microfone.</li>
            <li><strong>"O Windows não está permitindo que o Sokuji use o microfone"</strong>: as configurações de privacidade do Windows impedem que aplicativos da área de trabalho usem o microfone. Clique em <strong>Abrir Ajustes do Sistema</strong>, ative o acesso ao microfone para aplicativos da área de trabalho e inicie novamente.</li>
          </ul>
          Se o seu microfone não aparece na lista:
          <ol>
            <li>Abra Configurações do Windows → Privacidade e segurança → Microfone</li>
            <li>Certifique-se de que "Acesso ao microfone" e "Permitir que aplicativos da área de trabalho acessem seu microfone" estejam ativados</li>
            <li>Verifique se seu microfone está corretamente conectado e definido como dispositivo padrão</li>
          </ol>`,
        },
        {
          title: 'Sem saida de audio',
          content: `Se voce nao ouvir nenhum audio:
          <ol>
            <li>Clique com o botao direito no icone do alto-falante na bandeja do sistema</li>
            <li>Selecione "Abrir configuracoes de som"</li>
            <li>Verifique se seu dispositivo de saida esta corretamente selecionado</li>
            <li>Verifique se os niveis de volume nao estao silenciados</li>
            <li>No bloco <strong>Voz</strong> do Sokuji, confira o dispositivo da linha que você espera ouvir: <strong>Eu também ouço</strong> para a sua própria tradução (desativado por padrão), <strong>Tradução que eu ouço</strong> para a do outro lado e <strong>Dispositivo de reprodução padrão</strong> para todo o resto</li>
          </ol>`,
        },
        {
          title: 'O aplicativo nao inicia',
          content: `Se o Sokuji nao iniciar:
          <ul>
            <li>Tente executar como Administrador (clique com o botao direito → Executar como administrador)</li>
            <li>Verifique se o software antivirus esta bloqueando o aplicativo</li>
            <li>Reinstale o aplicativo</li>
            <li>Certifique-se de ter as ultimas atualizacoes do Windows instaladas</li>
          </ul>`,
        },
        {
          title: 'Dispositivos VB-CABLE nao detectados',
          content: `Se voce nao ver os dispositivos CABLE Output/Input no Sokuji:
          <ol>
            <li><strong>Reinicie seu computador</strong> - Isso geralmente resolve problemas de reconhecimento de dispositivos</li>
            <li><strong>Verifique as Configuracoes de Som do Windows</strong>:
              <ul>
                <li>Clique com o botao direito no icone do alto-falante na bandeja do sistema</li>
                <li>Selecione "Configuracoes de som"</li>
                <li>Procure "CABLE Input" nos dispositivos de reproducao</li>
                <li>Procure "CABLE Output" nos dispositivos de gravacao</li>
              </ul>
            </li>
            <li><strong>Reinstale o VB-CABLE manualmente</strong>:
              <ul>
                <li>Baixe de <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Extraia o arquivo ZIP</li>
                <li>Execute VBCABLE_Setup_x64.exe como Administrador</li>
                <li>Reinicie seu computador apos a instalacao</li>
              </ul>
            </li>
            <li><strong>Verifique o Gerenciador de Dispositivos</strong>:
              <ul>
                <li>Abra o Gerenciador de Dispositivos (Win+X → Gerenciador de Dispositivos)</li>
                <li>Procure em "Controladores de som, video e jogos"</li>
                <li>Verifique se "VB-Audio Virtual Cable" esta listado sem erros</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  ru: {
    pageTitle: 'Руководство по установке на Windows',
    overview:
      'Это руководство поможет вам установить Sokuji на системы Windows. Sokuji доступен в виде установщика .exe для легкой установки на Windows 10 и Windows 11.',
    downloadButton: 'Скачать для Windows',
    requirements: {
      title: 'Системные требования',
      items: [
        'Windows 10 (версия 1903 или новее) или Windows 11',
        '64-разрядный процессор',
        'Минимум 4 ГБ оперативной памяти (рекомендуется 8 ГБ)',
        '200 МБ свободного дискового пространства',
        'Подключение к интернету для служб перевода с ИИ',
        'Микрофон и динамики/наушники',
      ],
    },
    steps: [
      {
        title: 'Шаг 1: Скачать Sokuji',
        content: `Посетите <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">официальную страницу релизов на GitHub</a>, чтобы скачать последнюю версию Sokuji для Windows.`,
        subsections: [
          {
            title: 'Выберите правильный установщик',
            content: `<ul>
              <li><strong>Установщик .exe</strong> - Стандартный установщик для Windows</li>
              <li><strong>Портативная версия</strong> - Не требует установки (если доступна)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Шаг 2: Запустить установщик',
        content: `После скачивания найдите файл установщика и запустите его для начала установки.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>При запуске установщика Windows Defender SmartScreen может заблокировать запуск приложения:</p>
            <img src="/tutorials/windows-install/1.png" alt="Предупреждение Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Нажмите <strong>"Подробнее"</strong> в предупреждении SmartScreen</li>
              <li>Окно развернется, показывая больше деталей:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Расширенный вид Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Нажмите <strong>"Выполнить в любом случае"</strong>, чтобы продолжить установку</li>
            </ol>`,
          },
        ],
        warning:
          'Установщик Sokuji подписан через SignPath Foundation. Если SmartScreen всё же показывает предупреждение, шаги выше позволят продолжить.',
      },
      {
        title: 'Шаг 3: Установить виртуальное аудиоустройство VB-CABLE',
        content: `Sokuji требует виртуальное аудиоустройство VB-CABLE для маршрутизации аудио в другие приложения. Установщик предложит установить его автоматически.`,
        subsections: [
          {
            title: 'Установка VB-CABLE',
            content: `<p>После обхода SmartScreen вы увидите запрос на установку VB-CABLE:</p>
            <img src="/tutorials/windows-install/3.png" alt="Запрос установки VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Нажмите <strong>"Install Now"</strong> для автоматической загрузки и установки VB-CABLE</li>
              <li>Или нажмите "Download Manually", чтобы получить его с <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>Когда появится запрос контроля учетных записей, нажмите <strong>"Да"</strong>, чтобы разрешить установку:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Запрос контроля учетных записей для VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>Откроется установщик VB-CABLE. Нажмите <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="Окно установщика VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Дождитесь завершения установки. Вы увидите сообщение об успехе:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="Установка VB-CABLE завершена" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Нажмите <strong>"OK"</strong> в диалоговом окне завершения установки</li>
              <li>Появится финальное подтверждение:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Подтверждение успешной установки VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Нажмите <strong>"OK"</strong> для завершения установки VB-CABLE</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE создает виртуальные аудиоустройства, которые позволяют Sokuji передавать переведенное аудио в приложения для видеоконференций, такие как Zoom, Teams или Google Meet.',
      },
      {
        title: 'Шаг 4: Настройка первого запуска',
        content: `После установки VB-CABLE Sokuji запустится автоматически, и вы увидите страницу настройки аудио:`,
        subsections: [
          {
            title: 'Проверка аудиоустройств',
            content: `<img src="/tutorials/windows-install/7.png" alt="Настройка аудио Sokuji с отображением устройств VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>Чтобы проверить, что VB-CABLE установлен, откройте <strong>параметры звука Windows</strong> (щёлкните правой кнопкой по значку динамика в области уведомлений → «Параметры звука»):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - должно отображаться среди устройств записи</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - должно отображаться среди устройств воспроизведения</li>
            </ul>
            <p><strong>Примечание:</strong> в списке устройств Sokuji эти устройства CABLE не отображаются, и так задумано. Sokuji скрывает их, чтобы избежать случайной обратной связи в звуке, и сам находит и использует VB-CABLE в фоновом режиме.</p>`,
          },
          {
            title: 'Завершение настройки',
            content: `<ol>
              <li>Настройте предпочитаемого поставщика ИИ (OpenAI, Google Gemini и т.д.)</li>
              <li>Введите API-ключ для выбранного поставщика</li>
              <li>Выберите исходный и целевой языки</li>
              <li>Протестируйте устройства ввода/вывода аудио</li>
              <li>Выберите физический микрофон в разделе <strong>Микрофон</strong></li>
              <li>В блоке <strong>Речь</strong> выберите динамики или наушники как <strong>Устройство воспроизведения по умолчанию</strong>. <strong>Я тоже слышу</strong> и <strong>Перевод, который слышу я</strong> могут звучать каждый на своём устройстве; <strong>Перевод, который слышит собеседник</strong> идёт в виртуальный микрофон CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji готов к использованию! Вы можете начать перевод в реальном времени, нажав кнопку начала сеанса.',
      },
    ],
    troubleshooting: {
      title: 'Устранение неполадок',
      issues: [
        {
          title: 'Windows Defender блокирует установку',
          content: `Если Windows Defender SmartScreen блокирует установку:
          <ol>
            <li>Нажмите "Подробнее" в предупреждении SmartScreen</li>
            <li>Нажмите "Выполнить в любом случае" для продолжения установки</li>
          </ol>
          SmartScreen может предупреждать о подписанном установщике, который встречал редко. Это не указывает на проблему безопасности.`,
        },
        {
          title: 'Микрофон не обнаружен или не открывается',
          content: `Если при запуске микрофон не открывается, Sokuji сообщает причину:
          <ul>
            <li><strong>"Микрофон «…» сейчас занят (…)"</strong>: микрофон использует другое приложение. Закройте указанное приложение и начните заново или нажмите <strong>Настройки</strong> и выберите другой микрофон.</li>
            <li><strong>"Windows не удалось открыть микрофон «…»"</strong>: возможно, его занимает другое приложение или его драйвер перестал отвечать. Закройте приложения, которые его используют, или переподключите его, затем начните заново; в <strong>Настройки</strong> можно также выбрать другой микрофон.</li>
            <li><strong>"Windows не разрешает Sokuji использовать микрофон"</strong>: параметры конфиденциальности Windows запрещают классическим приложениям доступ к микрофону. Нажмите <strong>Открыть Системные настройки</strong>, включите доступ к микрофону для классических приложений, затем начните заново.</li>
          </ul>
          Если микрофона вообще нет в списке:
          <ol>
            <li>Откройте Параметры Windows → Конфиденциальность и защита → Микрофон</li>
            <li>Убедитесь, что включены "Доступ к микрофону" и "Разрешить классическим приложениям доступ к микрофону"</li>
            <li>Проверьте, правильно ли подключен микрофон и установлен ли он как устройство по умолчанию</li>
          </ol>`,
        },
        {
          title: 'Нет аудиовыхода',
          content: `Если вы не слышите звук:
          <ol>
            <li>Щелкните правой кнопкой мыши по значку динамика в области уведомлений</li>
            <li>Выберите "Открыть параметры звука"</li>
            <li>Проверьте, правильно ли выбрано устройство вывода</li>
            <li>Проверьте, не отключен ли звук</li>
            <li>В блоке <strong>Речь</strong> в Sokuji проверьте устройство той строки, которую вы ожидаете услышать: <strong>Я тоже слышу</strong> для вашего собственного перевода (по умолчанию выключено), <strong>Перевод, который слышу я</strong> для перевода собеседника и <strong>Устройство воспроизведения по умолчанию</strong> для всего остального</li>
          </ol>`,
        },
        {
          title: 'Приложение не запускается',
          content: `Если Sokuji не запускается:
          <ul>
            <li>Попробуйте запустить от имени Администратора (правый клик → Запуск от имени администратора)</li>
            <li>Проверьте, не блокирует ли антивирус приложение</li>
            <li>Переустановите приложение</li>
            <li>Убедитесь, что установлены последние обновления Windows</li>
          </ul>`,
        },
        {
          title: 'Устройства VB-CABLE не обнаружены',
          content: `Если вы не видите устройства CABLE Output/Input в Sokuji:
          <ol>
            <li><strong>Перезагрузите компьютер</strong> - Это часто решает проблемы с распознаванием устройств</li>
            <li><strong>Проверьте настройки звука Windows</strong>:
              <ul>
                <li>Щелкните правой кнопкой мыши по значку динамика в области уведомлений</li>
                <li>Выберите "Параметры звука"</li>
                <li>Найдите "CABLE Input" в устройствах воспроизведения</li>
                <li>Найдите "CABLE Output" в устройствах записи</li>
              </ul>
            </li>
            <li><strong>Переустановите VB-CABLE вручную</strong>:
              <ul>
                <li>Скачайте с <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Распакуйте ZIP-файл</li>
                <li>Запустите VBCABLE_Setup_x64.exe от имени Администратора</li>
                <li>Перезагрузите компьютер после установки</li>
              </ul>
            </li>
            <li><strong>Проверьте Диспетчер устройств</strong>:
              <ul>
                <li>Откройте Диспетчер устройств (Win+X → Диспетчер устройств)</li>
                <li>Найдите в разделе "Звуковые, игровые и видеоустройства"</li>
                <li>Убедитесь, что "VB-Audio Virtual Cable" отображается без ошибок</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  de: {
    pageTitle: 'Windows-Installationsanleitung',
    overview:
      'Diese Anleitung fuehrt Sie durch die Installation von Sokuji auf Windows-Systemen. Sokuji ist als .exe-Installationsprogramm fuer eine einfache Installation unter Windows 10 und Windows 11 verfuegbar.',
    downloadButton: 'Fuer Windows herunterladen',
    requirements: {
      title: 'Systemanforderungen',
      items: [
        'Windows 10 (Version 1903 oder spaeter) oder Windows 11',
        '64-Bit-Prozessor',
        'Mindestens 4 GB RAM (8 GB empfohlen)',
        '200 MB verfuegbarer Festplattenspeicher',
        'Internetverbindung fuer KI-Uebersetzungsdienste',
        'Mikrofon und Lautsprecher/Kopfhoerer',
      ],
    },
    steps: [
      {
        title: 'Schritt 1: Sokuji herunterladen',
        content: `Besuchen Sie die <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">offizielle GitHub-Releases-Seite</a>, um die neueste Version von Sokuji fuer Windows herunterzuladen.`,
        subsections: [
          {
            title: 'Das richtige Installationsprogramm waehlen',
            content: `<ul>
              <li><strong>.exe-Installationsprogramm</strong> - Standard-Installationsprogramm fuer Windows</li>
              <li><strong>Portable Version</strong> - Keine Installation erforderlich (falls verfuegbar)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Schritt 2: Das Installationsprogramm ausfuehren',
        content: `Suchen Sie nach dem Download die Installationsdatei und fuehren Sie sie aus, um die Installation zu starten.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>Beim Ausfuehren des Installationsprogramms kann Windows Defender SmartScreen den Start der App verhindern:</p>
            <img src="/tutorials/windows-install/1.png" alt="Windows Defender SmartScreen-Warnung" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Klicken Sie auf <strong>"Weitere Informationen"</strong> in der SmartScreen-Warnung</li>
              <li>Das Fenster wird erweitert, um weitere Details anzuzeigen:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Erweiterte Ansicht von Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Klicken Sie auf <strong>"Trotzdem ausfuehren"</strong>, um mit der Installation fortzufahren</li>
            </ol>`,
          },
        ],
        warning:
          'Der Sokuji-Installer ist über die SignPath Foundation signiert. Falls SmartScreen trotzdem warnt, können Sie mit den Schritten oben fortfahren.',
      },
      {
        title: 'Schritt 3: Das virtuelle Audiogeraet VB-CABLE installieren',
        content: `Sokuji benoetigt das virtuelle Audiogeraet VB-CABLE, um Audio an andere Anwendungen weiterzuleiten. Das Installationsprogramm fordert Sie auf, es automatisch zu installieren.`,
        subsections: [
          {
            title: 'VB-CABLE-Installation',
            content: `<p>Nach dem Umgehen von SmartScreen sehen Sie die VB-CABLE-Installationsaufforderung:</p>
            <img src="/tutorials/windows-install/3.png" alt="VB-CABLE-Installationsaufforderung" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Klicken Sie auf <strong>"Install Now"</strong>, um VB-CABLE automatisch herunterzuladen und zu installieren</li>
              <li>Alternativ koennen Sie auf "Download Manually" klicken, um es von <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a> zu beziehen</li>
              <li>Wenn die Benutzerkontensteuerung erscheint, klicken Sie auf <strong>"Ja"</strong>, um die Installation zu erlauben:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Benutzerkontensteuerung fuer VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>Das VB-CABLE-Installationsprogramm wird geoeffnet. Klicken Sie auf <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="VB-CABLE-Installationsfenster" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Warten Sie, bis die Installation abgeschlossen ist. Sie sehen eine Erfolgsmeldung:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="VB-CABLE-Installation abgeschlossen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Klicken Sie auf <strong>"OK"</strong> im Dialog fuer die abgeschlossene Installation</li>
              <li>Die endgueltige Bestaetigung wird angezeigt:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Bestaetigung der erfolgreichen VB-CABLE-Installation" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Klicken Sie auf <strong>"OK"</strong>, um die VB-CABLE-Installation abzuschliessen</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE erstellt virtuelle Audiogeraete, die es Sokuji ermoeglichen, uebersetzte Audioinhalte an Videokonferenzanwendungen wie Zoom, Teams oder Google Meet weiterzuleiten.',
      },
      {
        title: 'Schritt 4: Ersteinrichtung',
        content: `Nach der VB-CABLE-Installation wird Sokuji automatisch gestartet und Sie sehen die Audio-Konfigurationsseite:`,
        subsections: [
          {
            title: 'Audiogeraete ueberpruefen',
            content: `<img src="/tutorials/windows-install/7.png" alt="Sokuji-Audio-Konfiguration mit VB-CABLE-Geraeten" class="install-page__screenshot" data-lightbox="true" />
            <p>Um zu prüfen, ob VB-CABLE erfolgreich installiert wurde, öffnen Sie die <strong>Windows-Soundeinstellungen</strong> (Rechtsklick auf das Lautsprechersymbol in der Taskleiste → „Soundeinstellungen“):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - Sollte unter den Aufnahmegeräten erscheinen</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - Sollte unter den Wiedergabegeräten erscheinen</li>
            </ul>
            <p><strong>Hinweis:</strong> In der Geräteliste von Sokuji sehen Sie diese CABLE-Geräte nicht, das ist beabsichtigt. Sokuji blendet sie aus, um versehentliche Audio-Rückkopplungsschleifen zu vermeiden, und erkennt und verwendet VB-CABLE automatisch im Hintergrund.</p>`,
          },
          {
            title: 'Einrichtung abschliessen',
            content: `<ol>
              <li>Konfigurieren Sie Ihren bevorzugten KI-Anbieter (OpenAI, Google Gemini usw.)</li>
              <li>Geben Sie Ihren API-Schluessel fuer den ausgewaehlten Anbieter ein</li>
              <li>Waehlen Sie Quell- und Zielsprache aus</li>
              <li>Testen Sie die Audio-Ein-/Ausgabegeraete</li>
              <li>Wählen Sie Ihr physisches Mikrofon unter <strong>Mikrofon</strong></li>
              <li>Wählen Sie im Block <strong>Sprache</strong> Ihre Lautsprecher oder Kopfhörer als <strong>Standard-Wiedergabegerät</strong>. <strong>Ich höre es auch</strong> und <strong>Übersetzung, die ich höre</strong> können jeweils auf einem eigenen Gerät spielen; <strong>Übersetzung, die das Gegenüber hört</strong> geht in das virtuelle Mikrofon CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji ist jetzt einsatzbereit! Sie koennen die Echtzeit-Uebersetzung starten, indem Sie auf die Schaltflaeche zum Starten der Sitzung klicken.',
      },
    ],
    troubleshooting: {
      title: 'Fehlerbehebung',
      issues: [
        {
          title: 'Windows Defender blockiert die Installation',
          content: `Wenn Windows Defender SmartScreen die Installation blockiert:
          <ol>
            <li>Klicken Sie auf "Weitere Informationen" in der SmartScreen-Warnung</li>
            <li>Klicken Sie auf "Trotzdem ausfuehren", um mit der Installation fortzufahren</li>
          </ol>
          SmartScreen kann auch bei einem signierten Installer warnen, den es selten gesehen hat. Das deutet nicht auf ein Sicherheitsproblem hin.`,
        },
        {
          title: 'Mikrofon nicht erkannt oder lässt sich nicht öffnen',
          content: `Wenn sich das Mikrofon beim Start nicht öffnen lässt, nennt Sokuji den Grund:
          <ul>
            <li><strong>"Das Mikrofon „…“ wird von … verwendet"</strong>: Eine andere App belegt es. Schließen Sie die genannte App und starten Sie erneut, oder klicken Sie auf <strong>Einstellungen</strong> und wählen Sie ein anderes Mikrofon.</li>
            <li><strong>"Windows konnte das Mikrofon „…“ nicht öffnen"</strong>: Möglicherweise belegt es eine andere App, oder sein Treiber reagiert nicht mehr. Schließen Sie Apps, die es verwenden, oder schließen Sie es neu an, und starten Sie dann erneut; unter <strong>Einstellungen</strong> können Sie auch ein anderes Mikrofon wählen.</li>
            <li><strong>"Windows erlaubt Sokuji nicht, das Mikrofon zu verwenden"</strong>: Die Datenschutzeinstellungen von Windows sperren das Mikrofon für Desktop-Apps. Klicken Sie auf <strong>Systemeinstellungen öffnen</strong>, aktivieren Sie den Mikrofonzugriff für Desktop-Apps und starten Sie dann erneut.</li>
          </ul>
          Wenn Ihr Mikrofon gar nicht in der Liste erscheint:
          <ol>
            <li>Öffnen Sie Windows-Einstellungen → Datenschutz und Sicherheit → Mikrofon</li>
            <li>Stellen Sie sicher, dass "Mikrofonzugriff" und "Desktop-Apps den Zugriff auf Ihr Mikrofon erlauben" aktiviert sind</li>
            <li>Überprüfen Sie, ob Ihr Mikrofon richtig angeschlossen und als Standardgerät eingestellt ist</li>
          </ol>`,
        },
        {
          title: 'Keine Audioausgabe',
          content: `Wenn Sie keinen Ton hoeren:
          <ol>
            <li>Klicken Sie mit der rechten Maustaste auf das Lautsprechersymbol in der Taskleiste</li>
            <li>Waehlen Sie "Soundeinstellungen oeffnen"</li>
            <li>Ueberpruefen Sie, ob Ihr Ausgabegeraet korrekt ausgewaehlt ist</li>
            <li>Ueberpruefen Sie, ob die Lautstaerke nicht stummgeschaltet ist</li>
            <li>Prüfen Sie im Block <strong>Sprache</strong> in Sokuji das Gerät der Zeile, die Sie hören möchten: <strong>Ich höre es auch</strong> für Ihre eigene Übersetzung (standardmäßig aus), <strong>Übersetzung, die ich höre</strong> für die des Gegenübers und <strong>Standard-Wiedergabegerät</strong> für alles andere</li>
          </ol>`,
        },
        {
          title: 'Anwendung startet nicht',
          content: `Wenn Sokuji nicht startet:
          <ul>
            <li>Versuchen Sie, als Administrator auszufuehren (Rechtsklick → Als Administrator ausfuehren)</li>
            <li>Ueberpruefen Sie, ob Antivirensoftware die Anwendung blockiert</li>
            <li>Installieren Sie die Anwendung neu</li>
            <li>Stellen Sie sicher, dass die neuesten Windows-Updates installiert sind</li>
          </ul>`,
        },
        {
          title: 'VB-CABLE-Geraete nicht erkannt',
          content: `Wenn Sie die CABLE Output/Input-Geraete in Sokuji nicht sehen:
          <ol>
            <li><strong>Starten Sie Ihren Computer neu</strong> - Dies loest haeufig Probleme bei der Geraeteerkennung</li>
            <li><strong>Ueberpruefen Sie die Windows-Soundeinstellungen</strong>:
              <ul>
                <li>Klicken Sie mit der rechten Maustaste auf das Lautsprechersymbol in der Taskleiste</li>
                <li>Waehlen Sie "Soundeinstellungen"</li>
                <li>Suchen Sie nach "CABLE Input" in den Wiedergabegeraeten</li>
                <li>Suchen Sie nach "CABLE Output" in den Aufnahmegeraeten</li>
              </ul>
            </li>
            <li><strong>VB-CABLE manuell neu installieren</strong>:
              <ul>
                <li>Laden Sie es von <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a> herunter</li>
                <li>Entpacken Sie die ZIP-Datei</li>
                <li>Fuehren Sie VBCABLE_Setup_x64.exe als Administrator aus</li>
                <li>Starten Sie Ihren Computer nach der Installation neu</li>
              </ul>
            </li>
            <li><strong>Ueberpruefen Sie den Geraete-Manager</strong>:
              <ul>
                <li>Oeffnen Sie den Geraete-Manager (Win+X → Geraete-Manager)</li>
                <li>Schauen Sie unter "Audio-, Video- und Gamecontroller"</li>
                <li>Stellen Sie sicher, dass "VB-Audio Virtual Cable" ohne Fehler aufgefuehrt ist</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  uk: {
    pageTitle: 'Посібник зі встановлення на Windows',
    overview:
      'Цей посібник допоможе вам встановити Sokuji на системи Windows. Sokuji доступний як інсталятор .exe для легкого встановлення на Windows 10 та Windows 11.',
    downloadButton: 'Завантажити для Windows',
    requirements: {
      title: 'Системні вимоги',
      items: [
        'Windows 10 (версія 1903 або новіша) або Windows 11',
        '64-розрядний процесор',
        'Мінімум 4 ГБ оперативної памʼяті (рекомендовано 8 ГБ)',
        '200 МБ вільного дискового простору',
        'Підключення до інтернету для служб перекладу з ШІ',
        'Мікрофон та динаміки/навушники',
      ],
    },
    steps: [
      {
        title: 'Крок 1: Завантажити Sokuji',
        content: `Відвідайте <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">офіційну сторінку релізів на GitHub</a>, щоб завантажити останню версію Sokuji для Windows.`,
        subsections: [
          {
            title: 'Оберіть правильний інсталятор',
            content: `<ul>
              <li><strong>Інсталятор .exe</strong> - Стандартний інсталятор для Windows</li>
              <li><strong>Портативна версія</strong> - Не потребує встановлення (якщо доступна)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Крок 2: Запустити інсталятор',
        content: `Після завантаження знайдіть файл інсталятора та запустіть його для початку встановлення.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>При запуску інсталятора Windows Defender SmartScreen може заблокувати запуск програми:</p>
            <img src="/tutorials/windows-install/1.png" alt="Попередження Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Натисніть <strong>"Докладніше"</strong> у попередженні SmartScreen</li>
              <li>Вікно розгорнеться, показуючи більше деталей:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Розгорнутий вигляд Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Натисніть <strong>"Все одно запустити"</strong>, щоб продовжити встановлення</li>
            </ol>`,
          },
        ],
        warning:
          'Інсталятор Sokuji підписаний через SignPath Foundation. Якщо SmartScreen усе ж показує попередження, кроки вище дозволять продовжити.',
      },
      {
        title: 'Крок 3: Встановити віртуальний аудіопристрій VB-CABLE',
        content: `Sokuji потребує віртуальний аудіопристрій VB-CABLE для маршрутизації аудіо до інших програм. Інсталятор запропонує встановити його автоматично.`,
        subsections: [
          {
            title: 'Встановлення VB-CABLE',
            content: `<p>Після обходу SmartScreen ви побачите запит на встановлення VB-CABLE:</p>
            <img src="/tutorials/windows-install/3.png" alt="Запит встановлення VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Натисніть <strong>"Install Now"</strong> для автоматичного завантаження та встановлення VB-CABLE</li>
              <li>Або натисніть "Download Manually", щоб отримати його з <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>Коли зʼявиться запит контролю облікових записів, натисніть <strong>"Так"</strong>, щоб дозволити встановлення:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Запит контролю облікових записів для VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>Відкриється інсталятор VB-CABLE. Натисніть <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="Вікно інсталятора VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Зачекайте завершення встановлення. Ви побачите повідомлення про успіх:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="Встановлення VB-CABLE завершено" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Натисніть <strong>"OK"</strong> у діалоговому вікні завершення встановлення</li>
              <li>Зʼявиться фінальне підтвердження:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Підтвердження успішного встановлення VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Натисніть <strong>"OK"</strong> для завершення встановлення VB-CABLE</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE створює віртуальні аудіопристрої, які дозволяють Sokuji передавати перекладене аудіо до програм відеоконференцій, таких як Zoom, Teams або Google Meet.',
      },
      {
        title: 'Крок 4: Налаштування першого запуску',
        content: `Після встановлення VB-CABLE Sokuji запуститься автоматично, і ви побачите сторінку налаштування аудіо:`,
        subsections: [
          {
            title: 'Перевірка аудіопристроїв',
            content: `<img src="/tutorials/windows-install/7.png" alt="Налаштування аудіо Sokuji з відображенням пристроїв VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>Щоб перевірити, що VB-CABLE встановлено, відкрийте <strong>параметри звуку Windows</strong> (клацніть правою кнопкою значок динаміка в області сповіщень → «Параметри звуку»):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - має відображатися серед пристроїв запису</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - має відображатися серед пристроїв відтворення</li>
            </ul>
            <p><strong>Примітка:</strong> у списку пристроїв Sokuji ці пристрої CABLE не відображаються, і так задумано. Sokuji приховує їх, щоб уникнути випадкового зворотного зв'язку у звуку, і сам знаходить і використовує VB-CABLE у фоновому режимі.</p>`,
          },
          {
            title: 'Завершення налаштування',
            content: `<ol>
              <li>Налаштуйте бажаного постачальника ШІ (OpenAI, Google Gemini тощо)</li>
              <li>Введіть API-ключ для обраного постачальника</li>
              <li>Оберіть мову джерела та цільову мову</li>
              <li>Протестуйте пристрої введення/виведення аудіо</li>
              <li>Оберіть фізичний мікрофон у розділі <strong>Мікрофон</strong></li>
              <li>У блоці <strong>Мовлення</strong> оберіть динаміки або навушники як <strong>Пристрій відтворення за замовчуванням</strong>. <strong>Я теж чую</strong> і <strong>Переклад, який чую я</strong> можуть звучати кожен на своєму пристрої; <strong>Переклад, який чує співрозмовник</strong> іде у віртуальний мікрофон CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji готовий до використання! Ви можете розпочати переклад у реальному часі, натиснувши кнопку початку сеансу.',
      },
    ],
    troubleshooting: {
      title: 'Усунення несправностей',
      issues: [
        {
          title: 'Windows Defender блокує встановлення',
          content: `Якщо Windows Defender SmartScreen блокує встановлення:
          <ol>
            <li>Натисніть "Докладніше" у попередженні SmartScreen</li>
            <li>Натисніть "Все одно запустити" для продовження встановлення</li>
          </ol>
          SmartScreen може попереджати про підписаний інсталятор, який бачив рідко. Це не вказує на проблему безпеки.`,
        },
        {
          title: 'Мікрофон не виявлено або він не відкривається',
          content: `Якщо під час запуску мікрофон не відкривається, Sokuji повідомляє причину:
          <ul>
            <li><strong>"Мікрофон «…» зараз зайнятий (…)"</strong>: мікрофон використовує інша програма. Закрийте вказану програму й почніть знову або натисніть <strong>Налаштування</strong> та виберіть інший мікрофон.</li>
            <li><strong>"Windows не вдалося відкрити мікрофон «…»"</strong>: можливо, його використовує інша програма або його драйвер перестав відповідати. Закрийте програми, які його використовують, або під’єднайте його знову, а потім почніть знову; у <strong>Налаштування</strong> можна також вибрати інший мікрофон.</li>
            <li><strong>"Windows не дозволяє Sokuji використовувати мікрофон"</strong>: параметри конфіденційності Windows забороняють класичним програмам доступ до мікрофона. Натисніть <strong>Відкрити Системні параметри</strong>, увімкніть доступ до мікрофона для класичних програм, а потім почніть знову.</li>
          </ul>
          Якщо мікрофона взагалі немає у списку:
          <ol>
            <li>Відкрийте Параметри Windows → Конфіденційність і захист → Мікрофон</li>
            <li>Переконайтеся, що увімкнено "Доступ до мікрофона" і "Дозволити класичним програмам доступ до мікрофона"</li>
            <li>Перевірте, чи правильно підключений мікрофон та чи встановлений він як пристрій за замовчуванням</li>
          </ol>`,
        },
        {
          title: 'Немає аудіовиходу',
          content: `Якщо ви не чуєте звук:
          <ol>
            <li>Клацніть правою кнопкою миші на значку динаміка в області сповіщень</li>
            <li>Оберіть "Відкрити параметри звуку"</li>
            <li>Перевірте, чи правильно обрано пристрій виводу</li>
            <li>Перевірте, чи не вимкнено звук</li>
            <li>У блоці <strong>Мовлення</strong> в Sokuji перевірте пристрій того рядка, який ви очікуєте почути: <strong>Я теж чую</strong> для вашого власного перекладу (типово вимкнено), <strong>Переклад, який чую я</strong> для перекладу співрозмовника і <strong>Пристрій відтворення за замовчуванням</strong> для всього іншого</li>
          </ol>`,
        },
        {
          title: 'Програма не запускається',
          content: `Якщо Sokuji не запускається:
          <ul>
            <li>Спробуйте запустити від імені Адміністратора (правий клік → Запустити від імені адміністратора)</li>
            <li>Перевірте, чи не блокує антивірус програму</li>
            <li>Перевстановіть програму</li>
            <li>Переконайтеся, що встановлені останні оновлення Windows</li>
          </ul>`,
        },
        {
          title: 'Пристрої VB-CABLE не виявлено',
          content: `Якщо ви не бачите пристрої CABLE Output/Input в Sokuji:
          <ol>
            <li><strong>Перезавантажте компʼютер</strong> - Це часто вирішує проблеми з розпізнаванням пристроїв</li>
            <li><strong>Перевірте налаштування звуку Windows</strong>:
              <ul>
                <li>Клацніть правою кнопкою миші на значку динаміка в області сповіщень</li>
                <li>Оберіть "Параметри звуку"</li>
                <li>Знайдіть "CABLE Input" у пристроях відтворення</li>
                <li>Знайдіть "CABLE Output" у пристроях запису</li>
              </ul>
            </li>
            <li><strong>Перевстановіть VB-CABLE вручну</strong>:
              <ul>
                <li>Завантажте з <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Розпакуйте ZIP-файл</li>
                <li>Запустіть VBCABLE_Setup_x64.exe від імені Адміністратора</li>
                <li>Перезавантажте компʼютер після встановлення</li>
              </ul>
            </li>
            <li><strong>Перевірте Диспетчер пристроїв</strong>:
              <ul>
                <li>Відкрийте Диспетчер пристроїв (Win+X → Диспетчер пристроїв)</li>
                <li>Знайдіть у розділі "Звукові, ігрові та відеопристрої"</li>
                <li>Переконайтеся, що "VB-Audio Virtual Cable" відображається без помилок</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
  it: {
    pageTitle: 'Guida all\'installazione su Windows',
    overview:
      'Questa guida vi accompagnera nell\'installazione di Sokuji sui sistemi Windows. Sokuji e disponibile come programma di installazione .exe per un\'installazione semplice su Windows 10 e Windows 11.',
    downloadButton: 'Scarica per Windows',
    requirements: {
      title: 'Requisiti di sistema',
      items: [
        'Windows 10 (versione 1903 o successiva) o Windows 11',
        'Processore a 64 bit',
        '4 GB di RAM minimo (8 GB consigliati)',
        '200 MB di spazio su disco disponibile',
        'Connessione Internet per i servizi di traduzione con IA',
        'Microfono e altoparlanti/cuffie',
      ],
    },
    steps: [
      {
        title: 'Passaggio 1: Scaricare Sokuji',
        content: `Visitate la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina ufficiale delle release su GitHub</a> per scaricare l'ultima versione di Sokuji per Windows.`,
        subsections: [
          {
            title: 'Scegliere il programma di installazione corretto',
            content: `<ul>
              <li><strong>Programma di installazione .exe</strong> - Programma di installazione standard per Windows</li>
              <li><strong>Versione portatile</strong> - Non richiede installazione (se disponibile)</li>
            </ul>`,
          },
        ],
      },
      {
        title: 'Passaggio 2: Eseguire il programma di installazione',
        content: `Una volta scaricato, individuate il file del programma di installazione ed eseguitelo per iniziare l'installazione.`,
        subsections: [
          {
            title: 'Windows Defender SmartScreen',
            content: `<p>Quando eseguite il programma di installazione, Windows Defender SmartScreen potrebbe impedire l'avvio dell'app:</p>
            <img src="/tutorials/windows-install/1.png" alt="Avviso di Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Cliccate su <strong>"Ulteriori informazioni"</strong> nell'avviso SmartScreen</li>
              <li>La finestra si espandera per mostrare ulteriori dettagli:</li>
            </ol>
            <img src="/tutorials/windows-install/2.png" alt="Vista espansa di Windows Defender SmartScreen" class="install-page__screenshot" data-lightbox="true" />
            <ol start="3">
              <li>Cliccate su <strong>"Esegui comunque"</strong> per procedere con l'installazione</li>
            </ol>`,
          },
        ],
        warning:
          "L'installer di Sokuji è firmato tramite SignPath Foundation. Se SmartScreen mostra comunque un avviso, i passaggi qui sopra permettono di continuare.",
      },
      {
        title: 'Passaggio 3: Installare il dispositivo audio virtuale VB-CABLE',
        content: `Sokuji richiede il dispositivo audio virtuale VB-CABLE per instradare l'audio verso altre applicazioni. Il programma di installazione vi chiedera di installarlo automaticamente.`,
        subsections: [
          {
            title: 'Installazione di VB-CABLE',
            content: `<p>Dopo aver superato SmartScreen, vedrete il prompt di installazione di VB-CABLE:</p>
            <img src="/tutorials/windows-install/3.png" alt="Prompt di installazione VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol>
              <li>Cliccate su <strong>"Install Now"</strong> per scaricare e installare VB-CABLE automaticamente</li>
              <li>In alternativa, potete cliccare su "Download Manually" per ottenerlo da <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a></li>
              <li>Quando appare il prompt di Controllo dell'account utente, cliccate su <strong>"Si"</strong> per consentire l'installazione:</li>
            </ol>
            <img src="/tutorials/windows-install/3.5.jpg" alt="Prompt di Controllo dell'account utente per VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="4">
              <li>Il programma di installazione di VB-CABLE si aprira. Cliccate su <strong>"Install Driver"</strong>:</li>
            </ol>
            <img src="/tutorials/windows-install/4.png" alt="Finestra del programma di installazione VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="5">
              <li>Attendete il completamento dell'installazione. Vedrete un messaggio di successo:</li>
            </ol>
            <img src="/tutorials/windows-install/5.png" alt="Installazione VB-CABLE completata" class="install-page__screenshot" data-lightbox="true" />
            <ol start="6">
              <li>Cliccate su <strong>"OK"</strong> nella finestra di dialogo di installazione completata</li>
              <li>Apparira la conferma finale:</li>
            </ol>
            <img src="/tutorials/windows-install/6.png" alt="Conferma del successo dell'installazione VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <ol start="8">
              <li>Cliccate su <strong>"OK"</strong> per terminare l'installazione di VB-CABLE</li>
            </ol>`,
          },
        ],
        info: 'VB-CABLE crea dispositivi audio virtuali che consentono a Sokuji di trasmettere l\'audio tradotto alle applicazioni di videoconferenza come Zoom, Teams o Google Meet.',
      },
      {
        title: 'Passaggio 4: Configurazione del primo avvio',
        content: `Dopo l'installazione di VB-CABLE, Sokuji si avviera automaticamente e vedrete la pagina di configurazione audio:`,
        subsections: [
          {
            title: 'Verificare i dispositivi audio',
            content: `<img src="/tutorials/windows-install/7.png" alt="Configurazione audio di Sokuji che mostra i dispositivi VB-CABLE" class="install-page__screenshot" data-lightbox="true" />
            <p>Per verificare che VB-CABLE sia stato installato correttamente, aprite le <strong>Impostazioni audio di Windows</strong> (clic destro sull'icona dell'altoparlante nell'area di notifica → "Impostazioni audio"):</p>
            <ul>
              <li><strong>CABLE Output (VB-Audio Virtual Cable)</strong> - Dovrebbe comparire tra i dispositivi di registrazione</li>
              <li><strong>CABLE Input (VB-Audio Virtual Cable)</strong> - Dovrebbe comparire tra i dispositivi di riproduzione</li>
            </ul>
            <p><strong>Nota:</strong> questi dispositivi CABLE non compaiono nell'elenco dei dispositivi di Sokuji, ed è voluto. Sokuji li nasconde per evitare loop di feedback audio accidentali, e rileva e usa VB-CABLE automaticamente in background.</p>`,
          },
          {
            title: 'Completare la configurazione',
            content: `<ol>
              <li>Configurate il vostro fornitore di IA preferito (OpenAI, Google Gemini, ecc.)</li>
              <li>Inserite la vostra chiave API per il fornitore selezionato</li>
              <li>Selezionate le lingue di origine e di destinazione</li>
              <li>Testate i dispositivi di ingresso/uscita audio</li>
              <li>Selezionate il vostro microfono fisico in <strong>Microfono</strong></li>
              <li>Nel blocco <strong>Voce</strong>, scegliete i vostri altoparlanti o le cuffie come <strong>Dispositivo di riproduzione predefinito</strong>. <strong>Lo sento anche io</strong> e <strong>Traduzione che sento io</strong> possono suonare ciascuna su un proprio dispositivo; <strong>Traduzione che sente l'altra parte</strong> va nel microfono virtuale, CABLE Output (VB-Audio Virtual Cable)</li>
            </ol>`,
          },
        ],
        success:
          'Sokuji e ora pronto per l\'uso! Potete avviare la traduzione in tempo reale cliccando sul pulsante di avvio sessione.',
      },
    ],
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      issues: [
        {
          title: 'Windows Defender blocca l\'installazione',
          content: `Se Windows Defender SmartScreen blocca l'installazione:
          <ol>
            <li>Cliccate su "Ulteriori informazioni" nell'avviso SmartScreen</li>
            <li>Cliccate su "Esegui comunque" per procedere con l'installazione</li>
          </ol>
          SmartScreen può avvisare anche per un installer firmato che ha visto di rado. Questo non indica un problema di sicurezza.`,
        },
        {
          title: 'Microfono non rilevato o non si apre',
          content: `Se all'avvio il microfono non si apre, Sokuji ne indica il motivo:
          <ul>
            <li><strong>"Il microfono «…» è in uso da parte di …"</strong>: un'altra app lo sta usando. Chiudete l'app indicata e avviate di nuovo, oppure cliccate su <strong>Impostazioni</strong> e scegliete un altro microfono.</li>
            <li><strong>"Windows non è riuscito ad aprire il microfono «…»"</strong>: potrebbe usarlo un'altra app, oppure il suo driver ha smesso di rispondere. Chiudete le app che lo usano o ricollegatelo, poi avviate di nuovo; da <strong>Impostazioni</strong> potete anche scegliere un altro microfono.</li>
            <li><strong>"Windows non consente a Sokuji di usare il microfono"</strong>: le impostazioni di privacy di Windows impediscono alle app desktop di usare il microfono. Cliccate su <strong>Apri Impostazioni di Sistema</strong>, attivate l'accesso al microfono per le app desktop, poi avviate di nuovo.</li>
          </ul>
          Se il microfono non compare affatto nell'elenco:
          <ol>
            <li>Aprite Impostazioni di Windows → Privacy e sicurezza → Microfono</li>
            <li>Assicuratevi che "Accesso al microfono" e "Consenti alle app desktop di accedere al microfono" siano attivi</li>
            <li>Verificate che il microfono sia correttamente collegato e impostato come dispositivo predefinito</li>
          </ol>`,
        },
        {
          title: 'Nessuna uscita audio',
          content: `Se non sentite alcun audio:
          <ol>
            <li>Fate clic con il pulsante destro del mouse sull'icona dell'altoparlante nella barra delle applicazioni</li>
            <li>Selezionate "Apri impostazioni audio"</li>
            <li>Verificate che il dispositivo di uscita sia selezionato correttamente</li>
            <li>Verificate che i livelli del volume non siano disattivati</li>
            <li>Nel blocco <strong>Voce</strong> di Sokuji, controllate il dispositivo della riga che vi aspettate di sentire: <strong>Lo sento anche io</strong> per la vostra traduzione (disattivato per impostazione predefinita), <strong>Traduzione che sento io</strong> per quella dell'altra parte e <strong>Dispositivo di riproduzione predefinito</strong> per tutto il resto</li>
          </ol>`,
        },
        {
          title: 'L\'applicazione non si avvia',
          content: `Se Sokuji non si avvia:
          <ul>
            <li>Provate a eseguire come Amministratore (clic destro → Esegui come amministratore)</li>
            <li>Verificate se il software antivirus sta bloccando l'applicazione</li>
            <li>Reinstallate l'applicazione</li>
            <li>Assicuratevi di avere gli ultimi aggiornamenti di Windows installati</li>
          </ul>`,
        },
        {
          title: 'Dispositivi VB-CABLE non rilevati',
          content: `Se non vedete i dispositivi CABLE Output/Input in Sokuji:
          <ol>
            <li><strong>Riavviate il computer</strong> - Questo spesso risolve i problemi di riconoscimento dei dispositivi</li>
            <li><strong>Verificate le Impostazioni Audio di Windows</strong>:
              <ul>
                <li>Fate clic con il pulsante destro del mouse sull'icona dell'altoparlante nella barra delle applicazioni</li>
                <li>Selezionate "Impostazioni audio"</li>
                <li>Cercate "CABLE Input" nei dispositivi di riproduzione</li>
                <li>Cercate "CABLE Output" nei dispositivi di registrazione</li>
              </ul>
            </li>
            <li><strong>Reinstallate VB-CABLE manualmente</strong>:
              <ul>
                <li>Scaricate da <a href="https://vb-audio.com/Cable/" target="_blank">vb-audio.com/Cable</a></li>
                <li>Estraete il file ZIP</li>
                <li>Eseguite VBCABLE_Setup_x64.exe come Amministratore</li>
                <li>Riavviate il computer dopo l'installazione</li>
              </ul>
            </li>
            <li><strong>Verificate Gestione dispositivi</strong>:
              <ul>
                <li>Aprite Gestione dispositivi (Win+X → Gestione dispositivi)</li>
                <li>Guardate sotto "Controller audio, video e giochi"</li>
                <li>Verificate che "VB-Audio Virtual Cable" sia elencato senza errori</li>
              </ul>
            </li>
          </ol>`,
        },
      ],
    },
  },
};

export function WindowsInstall() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const openLightbox = (src: string, alt: string) => {
    setLightboxImage({ src, alt });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  // Handle click on images with data-lightbox attribute
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG' && target.getAttribute('data-lightbox') === 'true') {
      const img = target as HTMLImageElement;
      openLightbox(img.src, img.alt);
    }
  };

  return (
    <div className="docs-content install-page" onClick={handleContentClick}>
      <h1>{data.pageTitle}</h1>

      <p className="install-page__overview">{data.overview}</p>

      <a
        href="https://github.com/kizuna-ai-lab/sokuji/releases/latest"
        target="_blank"
        rel="noopener noreferrer"
        className="install-page__download-btn"
      >
        <Download size={20} />
        {data.downloadButton}
        <ExternalLink size={16} />
      </a>

      {/* System Requirements */}
      <div className="install-page__requirements">
        <h3>{data.requirements.title}</h3>
        <ul>
          {data.requirements.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Installation Steps */}
      {data.steps.map((step, stepIndex) => (
        <div key={stepIndex}>
          <h2>{step.title}</h2>
          <div className="install-page__step">
            <p dangerouslySetInnerHTML={{ __html: step.content }} />

            {step.subsections?.map((subsection, subIndex) => (
              <div key={subIndex}>
                <h4>{subsection.title}</h4>
                <div dangerouslySetInnerHTML={{ __html: subsection.content }} />
              </div>
            ))}

            {step.warning && <div className="install-page__warning">{step.warning}</div>}

            {step.info && <div className="install-page__info">{step.info}</div>}

            {step.success && <div className="install-page__success">{step.success}</div>}
          </div>
        </div>
      ))}

      {/* Troubleshooting */}
      <div className="install-page__troubleshooting">
        <h2>{data.troubleshooting.title}</h2>

        {data.troubleshooting.issues.map((issue, index) => (
          <div key={index} className="install-page__issue">
            <h3>{issue.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: issue.content }} />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          isOpen={!!lightboxImage}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}
